/**
 * AdminCRM — CHI Alliance POC
 * Back-office CRM view for admin users.
 * All actions (Edit, Add, Message) are non-functional POC stubs.
 *
 * Views:
 *   /admin            → Events overview table
 *   /admin?event=:id  → Attendee roster for a specific event
 */
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useLocation, Link } from "wouter";
import {
  Activity, CalendarDays, Users, MessageSquare, Settings, CreditCard,
  LogOut, Plus, Edit2, Eye, ChevronRight, ChevronLeft, Search,
  Mail, Phone, Linkedin, Shield, Download, Filter, X, CheckCircle,
  Clock, XCircle, ArrowUpRight, MapPin, Video, Save, RotateCcw, Loader2, UserRoundCheck,
  Archive, FilePenLine, MoreHorizontal, Trash2, Crown, UserPlus, UsersRound, Wrench,
  LayoutDashboard, Flag, ArrowLeft, Link as LinkIcon, Copy, ClipboardCheck, Globe,
} from "lucide-react";
import type { CHIEvent } from "@/lib/eventsData";
import { isPastCalendarEvent } from "@/lib/calendarFilters";
import { useEventClock } from "@/hooks/useEventClock";
import { useAuth } from "@/contexts/AuthContext";
import { EVENTS } from "@/lib/eventsData";
import { getEventSeatsRemaining } from "@/lib/eventCapacity";
import { paidEventCatalogRefetchInterval } from "@/lib/eventCatalogFreshness";
import CrmColumnHeading from "@/components/CrmColumnHeading";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import DeclineRegistrationButton from "@/components/DeclineRegistrationButton";
import { crmHelperTextStyle, crmInputStyle, crmLabelStyle } from "@/components/crmFormStyles";
import GoogleWorkspacePanel from "@/components/GoogleWorkspacePanel";
import MembershipPricePanel from "@/components/MembershipPricePanel";
import EventRegistrationTypeControl from "@/components/EventRegistrationTypeControl";
import SpecialEventApprovalControl, { type SpecialEventApprovalState } from "@/components/SpecialEventApprovalControl";
import MaintenanceView from "@/components/MaintenanceView";
import WebsiteContentView from "@/components/WebsiteContentView";
import TicketFlagsView from "@/components/TicketFlagsView";
import type { WebsiteContentEditorView } from "@/lib/websiteContentPages";
import RegistrationApprovalQueueView from "@/components/RegistrationApprovalQueueView";
import {
  composeEventPlatform,
  parseEventPlatform,
  type EventPlatform,
  type EventPlatformParts,
} from "@shared/eventPlatform";
import MessageCampaignView from "@/components/MessageCampaignView";
import OperationsHealthView from "@/components/OperationsHealthView";
import SpeakerCRMView from "@/components/SpeakerCRMView";
import FeaturedSpeakersCRMView from "@/components/FeaturedSpeakersCRMView";
import CreateEventDialog from "@/components/CreateEventDialog";
import EventPlatformField from "@/components/EventPlatformField";
import { EventEditStepControls, EventEditTabRail } from "@/components/EventEditTabs";
import CrmFilterTiles from "@/components/CrmFilterTiles";
import { administratorEventArtwork, eventArtOrPlaceholder, eventArtworkNeedsSpeakerReview, eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import { isSimulatedZoomJoinUrl } from "@shared/zoomLinks";
import { CRM_ROW_PORTRAIT_SIZE } from "@/components/PersonPortraitAvatar";
import AddEventAttendeesDialog from "@/components/AddEventAttendeesDialog";
import { canManuallyReviewEventRosterRow, canShowMemberProfileAction, filterEventRosterRows, mapEventRosterRows, type EventAttendeeRow, type EventAttendeeStatus, type EventRosterApiRow } from "@/components/adminEventRoster";
import LinkZoomMeetingDialog from "@/components/LinkZoomMeetingDialog";
import PaymentRecordsView from "@/components/PaymentRecordsView";
import MembershipManagementView from "@/components/MembershipManagementView";
import NotificationBell from "@/components/NotificationBell";
import MemberGroupManagementView from "@/components/MemberGroupManagementView";
import DataTableActions, { type DataTableAction } from "@/components/DataTableActions";
import ConnectorSettings from "@/components/ConnectorSettings";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import SearchableSelect from "@/components/SearchableSelect";
import ResponsiveImage from "@/components/ResponsiveImage";
import ImageUploader from "@/components/ImageUploader";
import EventResourceManager from "@/components/EventResourceManager";
import CommunityHeroControlsView from "@/components/CommunityHeroControlsView";
import EventDocumentsEditor from "@/components/EventDocumentsEditor";
import EventVideoPlayer from "@/components/EventVideoPlayer";
import { resolveEventVideoSource } from "@shared/eventVideo";
import { CHI_EVENT_CATEGORIES } from "@shared/eventCategories";
import { IMAGE_PRESETS_BY_KEY, type ImagePresetKey } from "@shared/imagePresets";
import {
  US_STATES,
  parseEventLocation,
  formatEventLocation,
  formatEventLocationDisplay,
  type EventLocationParts,
} from "@shared/eventLocation";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type EventGraphicAdminStatus = "not_requested" | "processing" | "ready" | "failed";
type AdminEventGraphic = {
  eventSlug: string;
  title: string;
  source: "managed" | "catalog";
  status: EventGraphicAdminStatus;
  attemptCount: number;
  lastError: string | null;
  updatedAt: string | Date | null;
  publicUrl: string | null;
  variants: Array<{
    id: number;
    url: string;
    width: number | null;
    height: number | null;
    generatedAt: string | Date;
  }>;
  heroGraphic?: {
    status: EventGraphicAdminStatus;
    attemptCount: number;
    lastError: string | null;
    updatedAt: string | Date | null;
    publicUrl: string | null;
    generatedAt: string | Date | null;
    logoUrl: string | null;
  };
};
type AdminEvent = CHIEvent & {
  isManaged: boolean;
  graphic?: AdminEventGraphic;
  /** Present only while the special_event_approval flag is on. */
  specialEventApproval?: SpecialEventApprovalState;
};

function escapeCsvCell(value: string | number | Date | null | undefined) {
  const normalized = value instanceof Date ? value.toISOString() : String(value ?? "");
  // Neutralize spreadsheet formulas while preserving the visible value in Excel
  // and similar applications.
  const safe = /^[\s\uFEFF]*[=+\-@]/.test(normalized) ? `'${normalized}` : normalized;
  return `"${safe.replace(/"/g, '""')}"`;
}

function buildEventAttendeeCsv(
  rows: Array<{
    fullName: string;
    email: string;
    company: string | null;
    jobTitle: string | null;
    registrationState: string;
    attendanceState: string;
    guestStatus: string;
    registeredAt: Date | string | null;
    rowKind?: "member" | "purchaser" | "guest";
    purchaserName?: string | null;
    isTicketBacked?: boolean;
  }>,
) {
  const header = [
    "Full name",
    "Email",
    "Company",
    "Job title",
    "Registration status",
    "Attendance status",
    "Guest status",
    "Attendee type",
    "Guest relationship",
    "Purchaser name",
    "Ticket-backed",
    "Registered at (UTC)",
  ];
  const values = rows.map(row => [
    row.fullName,
    row.email,
    row.company,
    row.jobTitle,
    row.registrationState,
    row.attendanceState,
    row.guestStatus,
    row.rowKind ?? "member",
    row.rowKind === "guest" ? `Guest of ${row.purchaserName ?? "ticket purchaser"}` : "",
    row.purchaserName ?? "",
    row.isTicketBacked ? "Yes" : "No",
    row.registeredAt,
  ]);

  return [header, ...values].map(row => row.map(escapeCsvCell).join(",")).join("\r\n");
}

// ── DateTimePicker ────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];

function DateTimePicker({
  dateValue, timeValue, onDateChange, onTimeChange, accentColor = "#E91E8C"
}: {
  dateValue: string; timeValue: string;
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  accentColor?: string;
}) {
  // Parse current values
  const parsedDate = dateValue ? new Date(dateValue + "T12:00:00") : new Date();
  const [viewYear, setViewYear] = useState(parsedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsedDate.getMonth());

  // Time state — parse from timeValue like "6:00 PM" or "6:00 PM – 8:00 PM"
  const parseTime = (raw: string) => {
    const match = raw.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (match) {
      let h = parseInt(match[1]);
      const m = parseInt(match[2]);
      const ampm = match[3].toUpperCase() as "AM" | "PM";
      return { h, m, ampm };
    }
    return { h: 6, m: 0, ampm: "PM" as "AM" | "PM" };
  };
  const parseEndTime = (raw: string) => {
    const parts = raw.split("–");
    if (parts.length > 1) return parseTime(parts[1].trim());
    const start = parseTime(raw);
    return { h: start.h + 2 > 12 ? start.h + 2 - 12 : start.h + 2, m: start.m, ampm: start.ampm };
  };

  const [startH, setStartH] = useState(parseTime(timeValue).h);
  const [startM, setStartM] = useState(parseTime(timeValue).m);
  const [startAMPM, setStartAMPM] = useState<"AM"|"PM">(parseTime(timeValue).ampm);
  const [endH, setEndH] = useState(parseEndTime(timeValue).h);
  const [endM, setEndM] = useState(parseEndTime(timeValue).m);
  const [endAMPM, setEndAMPM] = useState<"AM"|"PM">(parseEndTime(timeValue).ampm);

  const selectedDate = dateValue ? new Date(dateValue + "T12:00:00") : null;

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const today = new Date();

  function selectDay(day: number) {
    const m = String(viewMonth + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    onDateChange(`${viewYear}-${m}-${d}`);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  // Emit combined time string whenever time changes
  useEffect(() => {
    const fmt = (h: number, m: number, ampm: string) => `${h}:${String(m).padStart(2,"0")} ${ampm}`;
    onTimeChange(`${fmt(startH, startM, startAMPM)} – ${fmt(endH, endM, endAMPM)}`);
  }, [startH, startM, startAMPM, endH, endM, endAMPM]);

  const hours = [1,2,3,4,5,6,7,8,9,10,11,12];
  const minutes = [0,15,30,45];

  const inputS: React.CSSProperties = {
    background: "rgba(255,255,255,0.06)", border: `1px solid rgba(255,255,255,0.14)`,
    borderRadius: "0.5rem", color: "white", fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "var(--chi-body-compact)", outline: "none", cursor: "pointer",
  };

  const selStyle = (active: boolean): React.CSSProperties => ({
    ...inputS,
    padding: "0.4rem 0.6rem",
    background: active ? accentColor : "rgba(255,255,255,0.06)",
    border: `1px solid ${active ? accentColor : "rgba(255,255,255,0.14)"}`,
    color: active ? "white" : "#94A3B8",
    fontWeight: active ? 700 : 400,
    cursor: "pointer",
    transition: "all 0.15s ease",
  });

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === viewYear && selectedDate.getMonth() === viewMonth && selectedDate.getDate() === day;
  };
  const isToday = (day: number) => today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", gridColumn: "1 / -1" }}>
      {/* Calendar */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.875rem", padding: "1rem", gridColumn: "1 / 2" }}>
        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.75rem" }}>Event Date</p>
        {/* Month nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
          <button type="button" onClick={prevMonth} style={{ ...inputS, padding: "0.3rem 0.5rem", border: "none" }}><ChevronLeft size={14} /></button>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "white" }}>{MONTHS[viewMonth]} {viewYear}</span>
          <button type="button" onClick={nextMonth} style={{ ...inputS, padding: "0.3rem 0.5rem", border: "none" }}><ChevronRight size={14} /></button>
        </div>
        {/* Day headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", marginBottom: "4px" }}>
          {DAYS_SHORT.map(d => (
            <div key={d} style={{ textAlign: "center", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", padding: "0.25rem 0" }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const sel = isSelected(day);
            const tod = isToday(day);
            return (
              <button
                key={day}
                type="button"
                onClick={() => selectDay(day)}
                style={{
                  textAlign: "center", padding: "0.35rem 0",
                  borderRadius: "0.4rem", border: "none",
                  background: sel ? accentColor : tod ? "rgba(255,255,255,0.06)" : "transparent",
                  color: sel ? "white" : tod ? "white" : "#94A3B8",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "var(--chi-body-compact)", fontWeight: sel ? 700 : 400,
                  cursor: "pointer",
                  boxShadow: sel ? `0 0 12px ${accentColor}60` : "none",
                  outline: tod && !sel ? `1px solid rgba(255,255,255,0.2)` : "none",
                  transition: "all 0.12s ease",
                }}
                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = tod ? "rgba(255,255,255,0.06)" : "transparent"; }}
              >{day}</button>
            );
          })}
        </div>
        {/* Selected date preview */}
        {selectedDate && (
          <div style={{ marginTop: "0.75rem", padding: "0.5rem 0.75rem", background: `${accentColor}15`, border: `1px solid ${accentColor}30`, borderRadius: "0.5rem", textAlign: "center" }}>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: accentColor }}>
              {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </span>
          </div>
        )}
      </div>

      {/* Time pickers */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.875rem", padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase" }}>Event Time</p>

        {/* Start time */}
        <div>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B", marginBottom: "0.5rem" }}>Start Time</p>
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
            <select value={startH} onChange={e => setStartH(+e.target.value)} style={{ ...inputS, padding: "0.5rem 0.6rem" }}>
              {hours.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <span style={{ color: "#475569", fontWeight: 700 }}>:</span>
            <select value={startM} onChange={e => setStartM(+e.target.value)} style={{ ...inputS, padding: "0.5rem 0.6rem" }}>
              {minutes.map(m => <option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
            </select>
            <div style={{ display: "flex", gap: "0.25rem" }}>
              {(["AM","PM"] as const).map(p => (
                <button key={p} type="button" onClick={() => setStartAMPM(p)} style={selStyle(startAMPM === p)}>{p}</button>
              ))}
            </div>
          </div>
        </div>

        {/* End time */}
        <div>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B", marginBottom: "0.5rem" }}>End Time</p>
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
            <select value={endH} onChange={e => setEndH(+e.target.value)} style={{ ...inputS, padding: "0.5rem 0.6rem" }}>
              {hours.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <span style={{ color: "#475569", fontWeight: 700 }}>:</span>
            <select value={endM} onChange={e => setEndM(+e.target.value)} style={{ ...inputS, padding: "0.5rem 0.6rem" }}>
              {minutes.map(m => <option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
            </select>
            <div style={{ display: "flex", gap: "0.25rem" }}>
              {(["AM","PM"] as const).map(p => (
                <button key={p} type="button" onClick={() => setEndAMPM(p)} style={selStyle(endAMPM === p)}>{p}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div style={{ marginTop: "auto", padding: "0.625rem 0.875rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.5rem" }}>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Preview</p>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "white", fontWeight: 600 }}>
            {startH}:{String(startM).padStart(2,"0")} {startAMPM} – {endH}:{String(endM).padStart(2,"0")} {endAMPM}
          </p>
          {selectedDate && (
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B", marginTop: "0.15rem" }}>
              {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
const SIDEBAR_LINKS = [
  { icon: <Activity size={16} />, label: "Operations", view: "dashboard" },
  { icon: <CalendarDays size={16} />, label: "Events", view: "events" },
  { icon: <ClipboardCheck size={16} />, label: "Approvals", view: "approvals" },
  { icon: <UserRoundCheck size={16} />, label: "Speakers", view: "speakers" },
  { icon: <Crown size={16} />, label: "Members", view: "members" },
  { icon: <UsersRound size={16} />, label: "Groups", view: "groups" },
  { icon: <CreditCard size={16} />, label: "Payments", view: "payments" },
  { icon: <MessageSquare size={16} />, label: "Messages", view: "messages" },
  { icon: <Flag size={16} />, label: "Ticket flags", view: "ticket-flags" },
  { icon: <Globe size={16} />, label: "Website content", view: "website-content" },
  { icon: <LayoutDashboard size={16} />, label: "Community hero", view: "community-hero" },
  { icon: <Wrench size={16} />, label: "Maintenance", view: "maintenance" },
  { icon: <Settings size={16} />, label: "Settings", view: "settings" },
];

function Sidebar({ active, onNav }: { active: string; onNav: (v: string) => void }) {
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();

  return (
    <aside className="admin-crm-sidebar" style={{
      width: 220, minWidth: 220, height: "100vh", position: "sticky", top: 0,
      background: "rgba(10,14,28,0.98)", borderRight: "1px solid rgba(255,255,255,0.06)",
      display: "flex", flexDirection: "column", zIndex: 50,
    }}>
      {/* Logo */}
      <div className="admin-crm-sidebar__brand" style={{ padding: "1.25rem 1.25rem 1rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Link href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
          <img
            src="/media/brand/chi-flower-symbol.png"
            alt="The Chi Alliance"
            style={{ width: 120, height: "auto", objectFit: "contain" }}
          />
        </Link>
        <div style={{ marginTop: "0.5rem", display: "inline-flex", alignItems: "center", gap: "0.35rem", background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: "0.375rem", padding: "0.2rem 0.5rem" }}>
          <Shield size={10} style={{ color: "#A855F7" }} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#A855F7", letterSpacing: "0.06em", textTransform: "uppercase" }}>Admin</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="admin-crm-sidebar__nav" style={{ flex: 1, padding: "0.75rem 0.625rem", display: "flex", flexDirection: "column", gap: "0.125rem" }}>
        {SIDEBAR_LINKS.map(({ icon, label, view }) => {
          const isActive = active === view;
          return (
            <button
              key={view}
              onClick={() => onNav(view)}
              style={{
                display: "flex", alignItems: "center", gap: "0.65rem",
                padding: "0.6rem 0.75rem", borderRadius: "0.5rem", border: "none",
                background: isActive ? "rgba(233,30,140,0.12)" : "transparent",
                color: isActive ? "#E91E8C" : "#64748B",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: isActive ? 600 : 500,
                cursor: "pointer", textAlign: "left", width: "100%",
                transition: "all 0.15s ease",
                borderLeft: isActive ? "2px solid #E91E8C" : "2px solid transparent",
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "white"; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748B"; } }}
            >
              {icon} {label}
            </button>
          );
        })}
      </nav>

      {/* User + logout */}
      <div className="admin-crm-sidebar__user" style={{ padding: "0.875rem 1rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", marginBottom: "0.625rem" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#A855F7,#E91E8C)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Bebas Neue'", fontSize: "var(--chi-body-compact)", color: "white", flexShrink: 0 }}>
            {user?.avatar ?? "A"}
          </div>
          <div style={{ overflow: "hidden" }}>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name ?? "Admin"}</p>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email}</p>
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              await logout();
            } finally {
              navigate("/login");
            }
          }}
          aria-label="Sign out of the Admin CRM"
          title="Sign out"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", padding: "0.45rem 0.5rem", background: "transparent", border: "none", borderRadius: "0.375rem", color: "#EF4444", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 500, cursor: "pointer" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          <LogOut size={13} /> Sign Out
        </button>
      </div>
    </aside>
  );
}

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub: string; color: string }) {
  return (
    <div style={{ background: "rgba(13,18,32,0.8)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "0.875rem", padding: "1.35rem 1.6rem", flex: 1, minWidth: 160 }}>
      {/* The label stays uppercase and therefore small by the CRM legibility rule,
          but it was the smallest thing on the card — raised to the point where it
          still reads as a signpost without needing a squint. */}
      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: "#64748B", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.5rem" }}>{label}</p>
      <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.6rem", color, lineHeight: 1, marginBottom: "0.3rem" }}>{value}</p>
      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#475569" }}>{sub}</p>
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: EventAttendeeStatus }) {
  const map = {
    confirmed: { color: "#22C55E", bg: "rgba(34,197,94,0.1)", border: "rgba(34,197,94,0.25)", icon: <CheckCircle size={11} />, label: "Confirmed" },
    waitlist:  { color: "#F5A623", bg: "rgba(245,166,35,0.1)", border: "rgba(245,166,35,0.25)", icon: <Clock size={11} />, label: "Actual Waitlist" },
    review:    { color: "#60A5FA", bg: "rgba(96,165,250,0.1)", border: "rgba(96,165,250,0.25)", icon: <Clock size={11} />, label: "Needs review" },
    cancelled: { color: "#EF4444", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.25)", icon: <XCircle size={11} />, label: "Cancelled" },
    checkout_pending: { color: "#F5A623", bg: "rgba(245,166,35,0.1)", border: "rgba(245,166,35,0.25)", icon: <Clock size={11} />, label: "Checkout pending" },
    checkout_expired: { color: "#94A3B8", bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.2)", icon: <Clock size={11} />, label: "Checkout expired" },
    checkout_cancelled: { color: "#94A3B8", bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.2)", icon: <XCircle size={11} />, label: "Checkout canceled" },
    payment_failed: { color: "#EF7777", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.22)", icon: <XCircle size={11} />, label: "Payment failed" },
    ticket_review: { color: "#60A5FA", bg: "rgba(96,165,250,0.1)", border: "rgba(96,165,250,0.25)", icon: <Clock size={11} />, label: "Needs review" },
  };
  const s = map[status];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: s.bg, border: `1px solid ${s.border}`, borderRadius: "2rem", padding: "0.2rem 0.55rem", color: s.color, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600 }}>
      {s.icon} {s.label}
    </span>
  );
}

type CrmPortraitIdentity = {
  key: string;
  name: string;
  organization?: string | null;
  photoUrl?: string | null;
  memberPhotoUrl?: string | null;
};

function portraitInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return `${parts[0]?.[0] ?? ""}${parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : ""}`.toUpperCase();
}

function CrmPortraitAvatar({ identity, size = CRM_ROW_PORTRAIT_SIZE }: { identity: CrmPortraitIdentity; size?: number }) {
  const candidates = Array.from(new Set([identity.photoUrl, identity.memberPhotoUrl].filter(
    (value): value is string => Boolean(value),
  )));
  const candidateKey = candidates.join("\n");
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidateKey]);

  const activePhotoUrl = candidates[candidateIndex];
  const showPhoto = Boolean(activePhotoUrl);
  return (
    <span
      data-crm-portrait={showPhoto ? "photo" : "initials"}
      role="img"
      aria-label={showPhoto ? `${identity.name} portrait` : `${identity.name} initials`}
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        borderRadius: "50%",
        background: "linear-gradient(145deg, rgba(0,212,255,.22), rgba(168,85,247,.2))",
        border: "1px solid rgba(148,163,184,.28)",
        boxShadow: "0 8px 20px rgba(2,6,23,.28)",
        color: "#E2E8F0",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: Math.max(10, Math.round(size * 0.3)),
        fontWeight: 800,
        letterSpacing: "0.04em",
      }}
    >
      {showPhoto ? (
        <ResponsiveImage
          src={activePhotoUrl!}
          alt=""
          sizes={`${CRM_ROW_PORTRAIT_SIZE}px`}
          loading="lazy"
          onError={() => setCandidateIndex(index => index + 1)}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
        />
      ) : portraitInitials(identity.name)}
    </span>
  );
}

function EventSpeakerSummary({ speakers, compact = false }: { speakers: CrmPortraitIdentity[]; compact?: boolean }) {
  if (speakers.length === 0) return null;
  const visibleSpeakers = speakers.slice(0, 3);
  return (
    <div data-event-speaker-portraits style={{ display: "flex", alignItems: "center", gap: compact ? "0.45rem" : "0.7rem", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", paddingLeft: visibleSpeakers.length > 1 ? 6 : 0 }}>
        {visibleSpeakers.map((speaker, index) => (
          <span key={speaker.key} style={{ marginLeft: index === 0 ? 0 : -7, display: "inline-flex", position: "relative", zIndex: visibleSpeakers.length - index }}>
            <CrmPortraitAvatar identity={speaker} size={compact ? 35 : 53} />
          </span>
        ))}
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, color: compact ? "#94A3B8" : "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: compact ? "0.68rem" : "0.8rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {speakers.map(speaker => speaker.name).join(", ")}
        </p>
        {!compact && (
          <p style={{ margin: "0.12rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {speakers.length === 1 ? speakers[0]?.organization || "Event speaker" : `${speakers.length} assigned speakers`}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The events table's column track, declared once.
 *
 * The header and every row read the same string, because a table whose header and
 * body carry separate copies of a seven-column grid drifts the first time one is
 * edited — and the symptom is silently misaligned columns, not an error.
 *
 * The portrait column is a fixed width: it holds one row portrait, so it should
 * not flex with the viewport the way the text columns do. The track is wider than
 * the avatar on purpose — the surplus is the gutter that keeps a face off the
 * event title beside it, including when the stack holds several overlapped faces.
 */
/*
 * Date, Type and Registered hold a short fixed string each — "Sep 9, 2026", a
 * pill, "0 / 200" — so they are sized to their content rather than given an
 * equal share. Three greedy `1fr` columns were taking width from the event name,
 * which is the column that actually needs it and the one that truncates.
 */
/*
 * Featured Image and Registered are sized to their headings, not just their
 * contents: the column icons added width to every label, and "Registered" no
 * longer fitted 92px — it wrapped and took the whole header row to three lines
 * with it.
 *
 * The rest are sized to what they actually hold, measured rather than guessed, so
 * nothing is hoarding room the event name needs: the widest date renders at 106px,
 * the actions button at 94px.
 *
 * Every fixed track is wide enough for its *heading*, which is the wider of the two
 * things it must fit here. The headings are uppercase — they always were, in the
 * other CRM tables — so "FEATURED IMAGE" needs 169px and "REGISTERED" 125px, well
 * past what the thumbnail and the "0 / 200" beneath them ask for.
 */
const EVENT_ROW_COLUMNS = "68px minmax(0, 2fr) 178px minmax(0, 1.5fr) 110px 100px 132px 100px";

/**
 * Space between the event row's columns.
 *
 * The track had no gap at all, so every cell ran flush into the next — the
 * featured thumbnail touched the speaker names beside it, and the only relief
 * anywhere was a hand-placed right padding on the title. One gap on the grid
 * separates all eight columns consistently, and the narrow layout is unaffected
 * because it declares its own `gap` in index.css.
 *
 * For the spacing to *read* as equal, every cell also has to fill its track.
 * The Type pill was sized `fit-content`, so it stopped ~37px short of its column
 * edge and the gap before Registered looked like 51px next to everyone else's
 * 14px. Its column is now sized to the widest pill instead.
 */
const EVENT_ROW_COLUMN_GAP = "0.9rem";

/**
 * The event drawer's edit sections, in the order they step through.
 *
 * Grouped by the question being answered rather than by field type: "where and how
 * many" belongs together because capacity is a logistics decision, and the speaker
 * fields are one job that used to be split across the scroll.
 */
const EVENT_EDIT_TABS = [
  { id: "essentials", label: "Essentials" },
  { id: "location", label: "Location" },
  { id: "speakers", label: "Speakers" },
  { id: "content", label: "Content" },
  { id: "media", label: "Media" },
] as const;

type EventEditTabId = (typeof EVENT_EDIT_TABS)[number]["id"];

/**
 * The shared image shown in the Featured Image column when an event has not
 * received its own artwork yet. It is an actual 16:9 asset rather than a
 * stretched portrait or an empty image box, so the table reads as intentional
 * while future event-specific art is being prepared.
 */
const FEATURED_EVENT_FALLBACK_IMAGE = "/media/events/featured-session-stage.webp";

/**
 * The attendee table's column track, on the same principle as `EVENT_ROW_COLUMNS`.
 *
 * A leading column for the 50px portrait, so a person's face is the row's first
 * thing and every name below it lines up under its own heading. The track is
 * wider than the portrait deliberately: the surplus is the gutter that keeps the
 * face off the name and title beside it.
 */
const ATTENDEE_ROW_COLUMNS = "68px minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 1fr 1fr 150px";

/**
 * The stacked speaker portraits alone, for the events table's leading column.
 *
 * Split out of `EventSpeakerSummary` because the table now gives the faces and
 * the names their own columns, while the event preview drawer still wants them
 * together as one block. Both render the same avatars from the same identities;
 * only the arrangement differs.
 *
 * Carries `data-event-speaker-portraits`, which is the hook the people-photos
 * test asserts on.
 */
function EventSpeakerPortraits({ speakers }: { speakers: CrmPortraitIdentity[] }) {
  if (speakers.length === 0) {
    // A placeholder rather than nothing, so an event without an assigned speaker
    // keeps the column's width and every title in the table stays on one left edge.
    return <div data-event-speaker-portraits aria-hidden="true" style={{ width: CRM_ROW_PORTRAIT_SIZE, height: CRM_ROW_PORTRAIT_SIZE, borderRadius: "50%", border: "1px dashed rgba(255,255,255,.14)" }} />;
  }

  const visibleSpeakers = speakers.slice(0, 3);
  return (
    <div data-event-speaker-portraits style={{ display: "flex", alignItems: "center", paddingLeft: visibleSpeakers.length > 1 ? 6 : 0, minWidth: 0 }}>
      {visibleSpeakers.map((speaker, index) => (
        <span key={speaker.key} style={{ marginLeft: index === 0 ? 0 : -7, display: "inline-flex", position: "relative", zIndex: visibleSpeakers.length - index }}>
          <CrmPortraitAvatar identity={speaker} size={CRM_ROW_PORTRAIT_SIZE} />
        </span>
      ))}
    </div>
  );
}

/** The assigned speakers' names, for the events table column beside the event. */
function EventSpeakerNames({ speakers }: { speakers: CrmPortraitIdentity[] }) {
  if (speakers.length === 0) {
    return <span style={{ color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>No speaker assigned</span>;
  }

  return (
    <div style={{ minWidth: 0 }}>
      <p style={{ margin: 0, color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
        {speakers.map(speaker => speaker.name).join(", ")}
      </p>
      {/*
        The organisation earns its line only for a single speaker: for several it
        would be the wrong one, so the count is the honest second line instead.
      */}
      <p style={{ margin: "0.12rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
        {speakers.length === 1 ? speakers[0]?.organization || "Event speaker" : `${speakers.length} assigned speakers`}
      </p>
    </div>
  );
}

function useAdminEventSpeakerPortraits() {
  const speakersQuery = trpc.speakers.admin.list.useQuery(undefined, { retry: false });
  return useMemo(() => {
    const byEventSlug = new Map<string, CrmPortraitIdentity[]>();
    for (const record of speakersQuery.data ?? []) {
      const profile = record.profile;
      const identity: CrmPortraitIdentity = {
        key: String(profile.id),
        name: profile.fullName,
        organization: profile.organization,
        photoUrl: profile.crmPortraitUrl,
        memberPhotoUrl: profile.crmMemberPhotoUrl,
      };
      for (const assignment of record.assignments) {
        const current = byEventSlug.get(assignment.eventSlug) ?? [];
        current.push(identity);
        byEventSlug.set(assignment.eventSlug, current);
      }
    }
    return byEventSlug;
  }, [speakersQuery.data]);
}

function speakersForEvent(event: AdminEvent, byEventSlug: Map<string, CrmPortraitIdentity[]>) {
  const assigned = byEventSlug.get(event.slug);
  if (assigned?.length) return assigned;
  const speakerName = event.speaker?.trim();
  if (!speakerName || /^(tba|to be announced)$/i.test(speakerName)) return [];
  return [{ key: `event-${event.slug}`, name: speakerName, organization: event.speakerOrg, photoUrl: null }];
}

function EventGraphicStatus({
  graphic,
  onRetry,
  disabled = false,
  label = "Artwork",
}: {
  graphic?: Pick<AdminEventGraphic, "status" | "lastError"> & {
    generatedAt?: string | Date | null;
    logoUrl?: string | null;
  };
  onRetry?: () => void;
  disabled?: boolean;
  label?: string;
}) {
  const status = graphic?.status ?? "not_requested";
  const retryBlockedReason = graphic?.lastError?.includes("Add image-generation credits before retrying.")
    ? "Image-generation credits are required before retrying."
    : graphic?.lastError?.includes("Retrying will not help until that is set up.")
      ? "Image generation must be configured before retrying."
      : null;
  const labels: Record<EventGraphicAdminStatus, string> = {
    not_requested: `${label} not generated`,
    processing: `${label} processing`,
    ready: `${label} ready`,
    failed: `${label} failed`,
  };
  const colors: Record<EventGraphicAdminStatus, string> = {
    not_requested: "#94A3B8",
    processing: "#F5A623",
    ready: "#86EFAC",
    failed: "#FCA5A5",
  };
  const canRetry = Boolean(onRetry) && (status === "not_requested" || (status === "failed" && !retryBlockedReason));
  const generatedLabel = graphic?.generatedAt
    ? new Date(graphic.generatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", flexWrap: "wrap", color: colors[status], fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 650 }}>
      <span aria-label={retryBlockedReason ? `${labels[status]}. ${retryBlockedReason}` : labels[status]} title={graphic?.lastError ?? labels[status]} style={{ display: "inline-flex", alignItems: "center", gap: ".3rem" }}>
        {status === "processing" ? <Loader2 size={11} className="animate-spin" /> : status === "ready" ? <CheckCircle size={11} /> : status === "failed" ? <XCircle size={11} /> : <Clock size={11} />}
        {labels[status]}
        {status === "ready" && generatedLabel ? ` · ${generatedLabel}` : ""}
        {status === "ready" && graphic?.logoUrl ? " · logo included" : ""}
      </span>
      {status === "failed" && retryBlockedReason && (
        <span role="status" style={{ color: "#FDE68A", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 550 }}>
          {retryBlockedReason}
        </span>
      )}
      {canRetry && (
        <button
          type="button"
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onRetry?.();
          }}
          style={{ display: "inline-flex", alignItems: "center", gap: ".2rem", minHeight: 28, padding: "0 .45rem", border: `1px solid ${colors[status]}55`, borderRadius: ".4rem", background: `${colors[status]}12`, color: colors[status], fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: disabled ? "wait" : "pointer" }}
        >
          <RotateCcw size={10} />
          {status === "failed" ? "Retry" : "Generate"}
        </button>
      )}
    </span>
  );
}

function EventFeaturedImage({ event }: { event: AdminEvent }) {
  /*
   * The same resolver the public surfaces use, so what this column shows is what
   * the calendar tile and the event page show. It told an administrator "Using
   * uploaded Event Picture" for a picture the public never saw.
   */
  const primaryArtwork = eventFeaturedArtwork({
    slug: event.slug,
    title: event.title,
    eventImage: event.eventImage,
    heroImage: event.heroImage,
    generatedImage: event.graphic?.heroGraphic?.publicUrl,
  }, { width: 1600, height: 900 });
  const [fallbackLevel, setFallbackLevel] = useState(0);
  const needsSpeakerReview = eventArtworkNeedsSpeakerReview(event);

  /*
   * A stale error state must not carry from one event row to another when the
   * list refreshes in place. The URL is the identity of the primary candidate.
   */
  useEffect(() => {
    setFallbackLevel(0);
  }, [primaryArtwork.url]);

  const artwork = fallbackLevel === 0 && !primaryArtwork.isPlaceholder
    ? primaryArtwork
    : fallbackLevel < 2
      ? { url: FEATURED_EVENT_FALLBACK_IMAGE, isPlaceholder: true as const }
      : eventArtOrPlaceholder({
          slug: event.slug,
          title: event.title,
        }, { width: 1600, height: 900 });

  return (
    <div
      className="events-management-row__featured"
      data-featured-image
      title={needsSpeakerReview ? "Review this artwork: the named speaker must be the clearest primary person." : undefined}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        minWidth: 0,
        overflow: "hidden",
        border: needsSpeakerReview ? "2px solid #F5A623" : "1px solid rgba(255,255,255,.12)",
        borderRadius: ".45rem",
        background: "#080C18",
      }}
    >
      <ResponsiveImage
        src={artwork.url}
        alt={artwork.isPlaceholder ? `${event.title} featured image placeholder` : `${event.title} featured image`}
        sizes="132px"
        maxWidth={320}
        style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
        onError={() => setFallbackLevel(level => Math.min(level + 1, 2))}
      />
      {needsSpeakerReview && (
        <span style={{ position: "absolute", left: 5, right: 5, bottom: 5, padding: ".2rem .35rem", borderRadius: ".3rem", background: "rgba(8,12,24,.9)", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, lineHeight: 1.2, textAlign: "center" }}>
          Review speaker prominence
        </span>
      )}
    </div>
  );
}

// ── Event Detail / Edit Modal ─────────────────────────────────────────────────
function EventDetailModal({ event, onClose, onViewAttendees, startInEditMode = false, onSaved }: {
  event: AdminEvent;
  onClose: () => void;
  onViewAttendees: (id: number) => void;
  startInEditMode?: boolean;
  onSaved?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ ...event });
  const [specialEventDraft, setSpecialEventDraft] = useState(event.specialEventApproval?.isSpecialEvent ?? false);
  const pricingPoliciesQuery = trpc.events.pricingPolicies.useQuery(undefined, {
    enabled: Boolean(event.specialEventApproval),
    retry: false,
  });
  const savedPricingMode = pricingPoliciesQuery.data?.find(policy => policy.eventSlug === event.slug)?.pricingMode;
  const [zoomJoinUrl, setZoomJoinUrl] = useState("");
  /** Whether the archive confirmation is open for this event. */
  const [confirmArchive, setConfirmArchive] = useState(false);
  const utils = trpc.useUtils();
  const zoomMeetingQuery = trpc.events.adminZoomMeeting.useQuery({ eventSlug: event.slug }, { retry: false });
  const videoLibraryQuery = trpc.events.adminVideoLibrary.useQuery(undefined, {
    enabled: isEditing && event.isManaged,
    retry: false,
    staleTime: 30_000,
  });
  const speakersByEventSlug = useAdminEventSpeakerPortraits();
  const speakerRosterQuery = trpc.speakers.admin.list.useQuery(undefined, { retry: false });
  const drawerGraphicsQuery = trpc.events.adminGraphics.useQuery(undefined, { retry: false });
  const currentGraphic = drawerGraphicsQuery.data?.find(graphic => graphic.eventSlug === event.slug) ?? event.graphic;
  const assignedSpeakerTags = useMemo(() => {
    const byId = new Map<number, { id: number; name: string; organization: string | null }>();
    for (const record of speakerRosterQuery.data ?? []) {
      if (!record.assignments.some(assignment => assignment.eventSlug === event.slug)) continue;
      byId.set(record.profile.id, {
        id: record.profile.id,
        name: record.profile.fullName,
        organization: record.profile.organization,
      });
    }
    return Array.from(byId.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [event.slug, speakerRosterQuery.data]);
  const speakerChoices = useMemo(() => {
    const assignedIds = new Set(assignedSpeakerTags.map(speaker => speaker.id));
    const choices: { value: string; label: string; hint?: string }[] = [];
    for (const record of speakerRosterQuery.data ?? []) {
      const profile = record.profile;
      if (profile.status !== "approved" || assignedIds.has(profile.id)) continue;
      choices.push({
        value: String(profile.id),
        label: profile.fullName,
        hint: profile.organization ?? undefined,
      });
    }
    return choices.sort((left, right) => left.label.localeCompare(right.label));
  }, [assignedSpeakerTags, speakerRosterQuery.data]);

  useEffect(() => {
    setDraft({ ...event });
    setSpecialEventDraft(event.specialEventApproval?.isSpecialEvent ?? false);
    setFreeFormLocation(null);
    setLocationPlatform(null);
    setLocationDetailDraft(null);
    setEditTab("essentials");
    setZoomMode("idle");
  }, [event]);

  useEffect(() => {
    setZoomJoinUrl(zoomMeetingQuery.data?.joinUrl ?? "");
  }, [event.slug, zoomMeetingQuery.data?.joinUrl]);

  useEffect(() => {
    if (startInEditMode && event.isManaged) setIsEditing(true);
  }, [event.isManaged, startInEditMode]);

  /*
   * Archiving from inside the record being archived, rather than only from the
   * row menu. An administrator who has opened an event, read it, and decided it
   * should come down had to close the drawer and find the row again.
   */
  const archiveFromDrawer = trpc.events.archive.useMutation({
    onSuccess: async archived => {
      await Promise.all([
        utils.events.managedEvents.invalidate(),
        utils.events.archivedEvents.invalidate(),
        utils.events.publicCatalog.invalidate(),
      ]);
      setConfirmArchive(false);
      onSaved?.();
      onClose();
      chiNotify.success("Event archived", {
        description: `${archived.title} has been taken off the site. It is in the Archived tab.`,
      });
    },
    onError: error => {
      setConfirmArchive(false);
      chiNotify.error("Event not archived", { description: error.message });
    },
  });

  const updateEvent = trpc.events.update.useMutation({
    onSuccess: async updatedEvent => {
      await Promise.all([
        utils.events.publicCatalog.invalidate(),
        utils.events.managedEvents.invalidate(),
        utils.events.pricingPolicies.invalidate(),
      ]);
      chiNotify.success("Event updated", { description: `${updatedEvent.title} is now current across the event catalog.` });
      setIsEditing(false);
      onSaved?.();
      onClose();
    },
    onError: error => {
      chiNotify.error("Event not updated", { description: error.message });
    },
  });

  const assignEventSpeaker = trpc.speakers.admin.assignEvent.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker added to event");
    },
    onError: error => chiNotify.error("Speaker not added", { description: error.message }),
  });

  const unassignEventSpeaker = trpc.speakers.admin.unassignEvent.useMutation({
    onSuccess: async () => {
      await utils.speakers.admin.list.invalidate();
      chiNotify.success("Speaker removed from event", {
        description: "Their speaker profile and other event assignments remain unchanged.",
      });
    },
    onError: error => chiNotify.error("Speaker not removed", { description: error.message }),
  });

  const saveZoomMeeting = trpc.events.saveZoomMeeting.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.events.adminZoomMeeting.invalidate({ eventSlug: event.slug }),
        utils.events.myZoomJoinLink.invalidate({ eventSlug: event.slug }),
      ]);
      chiNotify.success("Zoom link saved", { description: `Registered attendees can now open the Zoom link for ${event.title}.` });
    },
    onError: error => chiNotify.error("Zoom link not saved", { description: error.message }),
  });
  const createZoomMeeting = trpc.events.createZoomMeeting.useMutation({
    onSuccess: async meeting => {
      await Promise.all([
        utils.events.adminZoomMeeting.invalidate({ eventSlug: event.slug }),
        utils.events.myZoomJoinLink.invalidate({ eventSlug: event.slug }),
      ]);
      setZoomJoinUrl(meeting.joinUrl);
      chiNotify.success("Zoom meeting ready", {
        description: `A scheduled attendee link was created from the date and time for ${event.title}.`,
      });
    },
    onError: error => chiNotify.error("Zoom meeting not created", { description: error.message }),
  });
  const removeZoomMeeting = trpc.events.removeZoomMeeting.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.events.adminZoomMeeting.invalidate({ eventSlug: event.slug }),
        utils.events.myZoomJoinLink.invalidate({ eventSlug: event.slug }),
      ]);
      setZoomJoinUrl("");
      chiNotify.success("Zoom link removed", { description: `Attendees can no longer open a Zoom meeting from ${event.title}.` });
    },
    onError: error => chiNotify.error("Zoom link not removed", { description: error.message }),
  });

  /**
   * Art uploaded during this editing session, offered as hero-image candidates.
   *
   * Session-scoped rather than read back from the event: the row stores one hero
   * URL, not a gallery, so the only place the other sizes an admin just cropped
   * exist is here. They are already in storage and their URLs were returned, so
   * nothing is lost by closing the drawer — but they stop being one click away.
   */
  const [uploadedArt, setUploadedArt] = useState<Array<{ url: string; presetKey: ImagePresetKey }>>([]);
  const uploadEventArt = trpc.events.uploadArt.useMutation({
    onSuccess: stored => chiNotify.success("Event Picture uploaded", {
      description: `Stored at ${stored.width} × ${stored.height}. Select it as the event picture, or keep it for social use.`,
    }),
    // Thrown on, not swallowed: the uploader's own row shows the failure and keeps
    // the file so the crop can be retried.
    onError: error => chiNotify.error("Event Picture not uploaded", { description: error.message }),
  });
  const deleteUploadedArt = trpc.events.deleteUploadedArt.useMutation({
    onSuccess: async removed => {
      setUploadedArt(current => current.filter(art => art.url !== removed.url));
      if (draft.heroImage === removed.url || removed.clearedHero) {
        setDraft(current => ({ ...current, heroImage: undefined }));
      }
      await Promise.all([
        utils.events.publicCatalog.invalidate(),
        utils.events.publicGraphic.invalidate({ eventSlug: event.slug }),
      ]);
      chiNotify.success("Uploaded image deleted");
    },
    onError: error => chiNotify.error("Uploaded image not deleted", { description: error.message }),
  });
  const uploadEventLogo = trpc.events.uploadLogo.useMutation({
    onSuccess: stored => chiNotify.success("Partner logo uploaded", {
      description: `Stored at ${stored.width} × ${stored.height}. It will be composited into the next generated hero image.`,
    }),
    onError: error => chiNotify.error("Partner logo not uploaded", { description: error.message }),
  });
  const deleteGeneratedArt = trpc.events.deleteGraphicVariant.useMutation({
    onSuccess: async removed => {
      if (draft.heroImage === removed.url) {
        setDraft(current => ({ ...current, heroImage: undefined }));
      }
      await Promise.all([
        utils.events.adminGraphics.invalidate(),
        utils.events.publicGraphic.invalidate({ eventSlug: event.slug }),
        utils.events.publicGraphics.invalidate(),
        utils.events.publicCatalog.invalidate(),
      ]);
      chiNotify.success("Generated image removed", { description: "The image is no longer available in this event’s Media gallery." });
    },
    onError: error => chiNotify.error("Generated image not removed", { description: error.message }),
  });

  /*
   * The location, as parts, derived from the draft rather than held beside it.
   *
   * Keeping a second copy in state would let the two disagree — the draft is what
   * saves, so if the parts were authoritative for the form and the string
   * authoritative for the mutation, an edit could be typed and then not sent. Parsing
   * on each render is cheap and keeps one source of truth.
   */
  const parsedLocation = useMemo(() => parseEventLocation(draft.location), [draft.location]);

  /**
   * Whether to edit the location as free text, when that differs from what parsing
   * says.
   *
   * Needed because unstructured-ness is otherwise *derived*, and a derived flag
   * cannot be overridden: "Dallas, TX" parses as a city and a state, so asking to
   * enter it as text would be undone by the next render. `null` means "follow the
   * parse", which is the case for every location until someone says otherwise.
   */
  const [freeFormLocation, setFreeFormLocation] = useState<boolean | null>(null);

  /**
   * The chosen platform, when that differs from what parsing says.
   *
   * Same reason `freeFormLocation` exists: a purely derived choice cannot be
   * overridden. "Other" contributes no prefix, so composing it with an empty
   * detail yields an empty string, which parses back as "nothing selected" --
   * the detail field would disappear as soon as its text was cleared, and could
   * never be reached at all on a location that starts blank. `null` means
   * "follow the parse", which is the case for every event until someone picks.
   */
  const [locationPlatform, setLocationPlatform] = useState<EventPlatform | "" | null>(null);

  /**
   * The joining detail while it is being typed.
   *
   * Reading it back out of the stored string means reading it through a parse
   * that trims, which makes a trailing space impossible to type — the same trap
   * `setLocation` documents for `formatEventLocation`. `null` means "follow the
   * parse", so an untouched event still shows what it has saved.
   */
  const [locationDetailDraft, setLocationDetailDraft] = useState<string | null>(null);

  /* Which edit section is showing. A new event always opens on the first. */
  const [editTab, setEditTab] = useState<EventEditTabId>("essentials");

  /**
   * Which way the Zoom link is being attached, or neither yet.
   *
   * "idle" is the resting state and shows only the choice, so opening an event
   * does not present two half-relevant controls at once.
   */
  const [zoomMode, setZoomMode] = useState<"idle" | "existing" | "new">("idle");

  /* Removing a link can delete a real meeting, so it is confirmed rather than immediate. */
  const [confirmRemoveZoom, setConfirmRemoveZoom] = useState(false);
  /*
   * Whether the stored meeting is one this workspace booked, which is what decides
   * if removing the link also deletes it in Zoom. A pasted link belongs to whoever
   * made it, and a simulated one has no meeting behind it.
   */
  const zoomMeetingIsSimulated = isSimulatedZoomJoinUrl(zoomMeetingQuery.data?.joinUrl);
  const zoomMeetingIsOurs = zoomMeetingQuery.data?.source === "zoom_api" && !zoomMeetingIsSimulated;

  /**
   * Writes a platform choice back to the draft's single location string.
   *
   * Pinned to free text while the dropdown is in use: "Other" with nothing typed
   * composes an empty string, and an empty location parses as structured — without
   * the pin, choosing "Other" swapped the editor out for Venue/City/State mid-edit.
   */
  const setPlatform = (parts: EventPlatformParts) => {
    setFreeFormLocation(true);
    setLocationPlatform(parts.platform);
    setLocationDetailDraft(parts.detail);
    setDraft(d => ({ ...d, location: composeEventPlatform(parts) }));
  };
  const locationParts: EventLocationParts = {
    ...parsedLocation,
    unstructured: freeFormLocation ?? parsedLocation.unstructured,
  };

  /**
   * Writes parts back to the draft as the single formatted string it stores.
   *
   * The free-text path assigns the raw value rather than formatting it, because
   * `formatEventLocation` trims — and trimming on every keystroke makes a trailing
   * space impossible to type.
   */
  const setLocation = (parts: EventLocationParts) =>
    setDraft(d => ({ ...d, location: parts.unstructured ? parts.raw : formatEventLocation(parts) }));

  const fill = Math.round((event.registered / event.seats) * 100);
  const eventSpeakers = speakersForEvent(event, speakersByEventSlug);

  function handleSave() {
    if (!event.isManaged) {
      chiNotify.info("Catalog event", { description: "This established catalog event is read-only in the Events management workspace." });
      return;
    }

    const eventDate = new Date(`${draft.date}T12:00:00`);
    const displayDate = Number.isNaN(eventDate.getTime())
      ? draft.displayDate
      : eventDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    updateEvent.mutate({
      slug: event.slug,
      eventDate: draft.date,
      displayDate,
      timeLabel: draft.time,
      title: draft.title.trim(),
      subtitle: draft.subtitle.trim(),
      eventType: draft.type === "in-person" ? "in_person" : "virtual",
      location: draft.location.trim(),
      speaker: draft.speaker.trim(),
      speakerOrg: draft.speakerOrg.trim(),
      color: draft.color,
      seats: draft.seats,
      description: draft.description.trim(),
      highlights: draft.highlights,
      agenda: draft.agenda,
      timezone: draft.timezone,
      joinUrl: draft.joinUrl,
      category: draft.category,
      speakerBio: draft.speakerBio?.trim() || undefined,
      portraitImageUrl: draft.portraitImage,
      heroImageUrl: draft.heroImage,
      partnerLogoUrl: draft.partnerLogo,
      eventVideoUrl: draft.videoUrl?.trim() || undefined,
      registrationRedirectUrl: draft.registrationRedirectUrl,
      ...(event.specialEventApproval && specialEventDraft !== event.specialEventApproval.isSpecialEvent
        ? { isSpecialEvent: specialEventDraft }
        : {}),
    });
  }

  function handleDiscard() {
    setDraft({ ...event });
    setSpecialEventDraft(event.specialEventApproval?.isSpecialEvent ?? false);
    setFreeFormLocation(null);
    setLocationPlatform(null);
    setLocationDetailDraft(null);
    setIsEditing(false);
  }

  /* Shared with the member drawer — see client/src/components/crmFormStyles.ts. */
  const inputStyle = crmInputStyle;
  const labelStyle = crmLabelStyle;

  return (
    /*
      A right-side drawer on the shared CRM skin, matching the member and speaker
      drawers. It was a centred modal with its actions in the header and an X in
      the corner — three ways to leave, none of them where a form's buttons
      belong. Escape and the backdrop close it; Discard and Save sit together in
      the footer where the form ends.
    */
    <Sheet open onOpenChange={next => { if (!next) onClose(); }}>
      <SheetContent side="right" className="speaker-create-drawer">
        <div className="crm-drawer">
          <SheetHeader className="crm-drawer__header">
            {/*
              The progress rail is the first editor element, above the title,
              subtitle, and every tab panel. Keeping it in the non-scrolling
              header also leaves the current section visible throughout editing.
            */}
            {isEditing && (
              <div data-event-edit-progress style={{ marginBottom: ".75rem" }}>
                <EventEditTabRail tabs={EVENT_EDIT_TABS} activeId={editTab} onChange={id => setEditTab(id as EventEditTabId)} />
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                {isEditing ? (
                  <>
                    {/*
                      The name and subtitle are edited where they are read, at the
                      top, rather than as two more rows inside a tab. They were the
                      only fields that appeared twice — once as the drawer's heading
                      and again in Essentials.

                      `SheetTitle` stays as the dialog's accessible name: Radix
                      requires one, and an input is not a heading.
                    */}
                    <SheetTitle className="sr-only">{event.title || "Edit event"}</SheetTitle>
                    <input
                      aria-label="Event title"
                      value={draft.title}
                      onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                      placeholder="Event title"
                      style={{ ...inputStyle, fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.5rem", letterSpacing: ".03em", padding: ".3rem .5rem" }}
                    />
                    <input
                      aria-label="Subtitle or partnership"
                      value={draft.subtitle}
                      onChange={e => setDraft(d => ({ ...d, subtitle: e.target.value }))}
                      placeholder="Subtitle or partnership (optional)"
                      style={{ ...inputStyle, marginTop: ".4rem", padding: ".3rem .5rem" }}
                    />
                  </>
                ) : (
                  <>
                    <SheetTitle className="crm-drawer__title">{event.title}</SheetTitle>
                    {event.subtitle && <SheetDescription className="crm-drawer__subtitle">{event.subtitle}</SheetDescription>}
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="crm-drawer__body">
          {/* Status badges */}
          {/*
            Hidden while editing. "Virtual" and "0% full" describe the saved record,
            and beside a form they read as fields you cannot change — the event type
            is edited in Essentials and the fill rate is not editable at all.
          */}
          {!isEditing && <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: event.type === "virtual" ? "#00D4FF" : "#F5A623", background: event.type === "virtual" ? "rgba(0,212,255,0.1)" : "rgba(245,166,35,0.1)", border: `1px solid ${event.type === "virtual" ? "rgba(0,212,255,0.25)" : "rgba(245,166,35,0.25)"}`, borderRadius: "2rem", padding: "0.25rem 0.75rem" }}>
              {event.type === "virtual" ? <Video size={11} /> : <MapPin size={11} />}
              {event.type === "virtual" ? "Virtual" : "In-Person"}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: fill >= 80 ? "#E91E8C" : fill >= 50 ? "#F5A623" : "#22C55E", background: fill >= 80 ? "rgba(233,30,140,0.1)" : fill >= 50 ? "rgba(245,166,35,0.1)" : "rgba(34,197,94,0.1)", border: `1px solid ${fill >= 80 ? "rgba(233,30,140,0.25)" : fill >= 50 ? "rgba(245,166,35,0.25)" : "rgba(34,197,94,0.25)"}`, borderRadius: "2rem", padding: "0.25rem 0.75rem" }}>
              {fill}% full
            </span>
          </div>}

          {!isEditing && eventSpeakers.length > 0 && (
            <section aria-label="Event speakers" style={{ marginBottom: "1.25rem", padding: "0.85rem 1rem", borderRadius: "0.75rem", background: "rgba(0,212,255,.045)", border: "1px solid rgba(0,212,255,.13)" }}>
              <p style={{ margin: "0 0 .55rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>Event speakers</p>
              <EventSpeakerSummary speakers={eventSpeakers} />
            </section>
          )}

          {isEditing ? (
            /* ── EDIT MODE ── */
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              {editTab === "essentials" && (
                <div
                  role="tabpanel"
                  id="event-edit-panel-essentials"
                  aria-labelledby="event-edit-tab-essentials"
                  style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", alignItems: "start" }}
                >
              <DateTimePicker
                dateValue={draft.date}
                timeValue={draft.time}
                onDateChange={v => setDraft(d => ({ ...d, date: v }))}
                onTimeChange={v => setDraft(d => ({ ...d, time: v }))}
                accentColor={event.color || "#E91E8C"}
              />
              <div>
                <label style={labelStyle}>Event Type</label>
                <select style={{ ...inputStyle, cursor: "pointer" }} value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value as "virtual" | "in-person" }))}>
                  <option value="virtual">Virtual</option>
                  <option value="in-person">In-Person</option>
                </select>
              </div>
              <div>
                {/*
                  Category drives the public filters and the speaker tag, and was
                  settable only in code — so an event created in the CRM was
                  uncategorised with no way to fix it.
                */}
                <label style={labelStyle} htmlFor="event-category">Category</label>
                <select
                  id="event-category"
                  style={{ ...inputStyle, cursor: "pointer" }}
                  value={draft.category ?? "Community"}
                  onChange={e => setDraft(d => ({ ...d, category: e.target.value as AdminEvent["category"] }))}
                >
                  {CHI_EVENT_CATEGORIES.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
                </div>
              )}
              {editTab === "location" && (
                <div
                  role="tabpanel"
                  id="event-edit-panel-location"
                  aria-labelledby="event-edit-tab-location"
                  style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", alignItems: "start" }}
                >
              {/*
                Location as parts: a venue line, a city, and a state chosen from a
                list, so those stop being free text every organizer punctuates
                differently. The stored value is still one string — see
                shared/eventLocation.ts for why, and for the parse that makes
                "The Board Studios, Dallas, TX" round-trip unchanged.

                A location that is not a place keeps a single free-text field. Six of
                the catalog's eight events read "Zoom (link sent after registration)",
                which has no city and never will, and forcing it through a city field
                would throw the detail away.
              */}
              {locationParts.unstructured ? (
                <div style={{ display: "grid", gap: ".5rem" }}>
                  <EventPlatformField
                    platform={locationPlatform ?? parseEventPlatform(locationParts.raw).platform}
                    detail={locationDetailDraft ?? parseEventPlatform(locationParts.raw).detail}
                    joinUrl={draft.joinUrl ?? ""}
                    onPlatformChange={(next, carriedDetail) => setPlatform({ platform: next, detail: carriedDetail })}
                    onDetailChange={next => setPlatform({
                      platform: locationPlatform ?? parseEventPlatform(locationParts.raw).platform,
                      detail: next,
                    })}
                    onJoinUrlChange={next => setDraft(d => ({ ...d, joinUrl: next || undefined }))}
                    fieldStyle={inputStyle}
                    labelStyle={{ ...labelStyle, display: "grid", gap: ".3rem" }}
                  />
                  <button
                    type="button"
                    onClick={() => setFreeFormLocation(false)}
                    style={{ marginTop: ".1rem", padding: 0, border: 0, background: "transparent", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer", justifySelf: "start" }}
                  >
                    Use city and state instead
                  </button>
                </div>
              ) : (
                <div style={{ display: "grid", gap: ".5rem" }}>
                  <div>
                    <label style={labelStyle} htmlFor="event-location-venue">Venue <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: "#64748B" }}>(optional)</span></label>
                    <input
                      id="event-location-venue"
                      style={inputStyle}
                      placeholder="Enter the venue name"
                      value={locationParts.venue}
                      onChange={e => setLocation({ ...locationParts, venue: e.target.value })}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 128px", gap: ".5rem" }}>
                    <div>
                      <label style={labelStyle} htmlFor="event-location-city">City</label>
                      <input
                        id="event-location-city"
                        style={inputStyle}
                        placeholder="Enter the city"
                        value={locationParts.city}
                        onChange={e => setLocation({ ...locationParts, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={labelStyle} htmlFor="event-location-state">State</label>
                      <select
                        id="event-location-state"
                        style={{ ...inputStyle, cursor: "pointer" }}
                        value={locationParts.state}
                        onChange={e => setLocation({ ...locationParts, state: e.target.value })}
                      >
                        <option value="">—</option>
                        {US_STATES.map(state => (
                          <option key={state.code} value={state.code}>{state.code}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFreeFormLocation(true)}
                    style={{ justifySelf: "start", padding: 0, border: 0, background: "transparent", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                  >
                    This location is not a city — enter it as text
                  </button>
                </div>
              )}
              <div>
                <label style={labelStyle}>Total Seats</label>
                <input style={inputStyle} type="number" value={draft.seats} onChange={e => setDraft(d => ({ ...d, seats: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <span style={labelStyle}>Registered Count</span>
                <div style={{ ...inputStyle, color: "#94A3B8", background: "rgba(255,255,255,0.03)" }}>{event.registered} — calculated from attendee registrations</div>
              </div>
                </div>
              )}
              {editTab === "speakers" && (
                <div
                  role="tabpanel"
                  id="event-edit-panel-speakers"
                  aria-labelledby="event-edit-tab-speakers"
                  style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", alignItems: "start" }}
                >
              <div style={{ gridColumn: "1 / -1" }}>
                <style>{`
                  .event-speaker-tags { display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:.65rem; }
                  .event-speaker-tag { display:inline-flex; align-items:center; gap:.45rem; min-height:34px; max-width:100%; padding:.38rem .45rem .38rem .7rem; border:1px solid rgba(125,234,255,.28); border-radius:999px; background:rgba(125,234,255,.08); color:#DDFBFF; font:700 var(--chi-body-compact) 'Space Grotesk',sans-serif; }
                  .event-speaker-tag__name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
                  .event-speaker-tag__remove { display:grid; place-items:center; width:24px; height:24px; padding:0; border:0; border-radius:50%; background:transparent; color:#A7B5C8; opacity:.28; cursor:pointer; transition:opacity .16s ease,background .16s ease,color .16s ease; }
                  .event-speaker-tag:hover .event-speaker-tag__remove, .event-speaker-tag__remove:hover, .event-speaker-tag__remove:focus-visible { opacity:1; background:rgba(248,113,113,.14); color:#FCA5A5; outline:none; }
                  .event-speaker-tag__remove:disabled { cursor:wait; opacity:.45; }
                `}</style>
                <label style={labelStyle}>Speakers</label>
                {assignedSpeakerTags.length > 0 && (
                  <div className="event-speaker-tags" aria-label="Assigned speakers">
                    {assignedSpeakerTags.map(speaker => {
                      const removing = unassignEventSpeaker.isPending
                        && unassignEventSpeaker.variables?.speakerId === speaker.id;
                      return (
                        <span className="event-speaker-tag" key={speaker.id}>
                          <span className="event-speaker-tag__name">{speaker.name}</span>
                          <button
                            type="button"
                            className="event-speaker-tag__remove"
                            disabled={removing}
                            aria-label={`Remove ${speaker.name} from this event`}
                            title={`Remove ${speaker.name}`}
                            onClick={() => unassignEventSpeaker.mutate({
                              speakerId: speaker.id,
                              eventSlug: event.slug,
                            })}
                          >
                            <X size={13} aria-hidden="true" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <SearchableSelect
                  ariaLabel="Add speaker"
                  placeholder={assignedSpeakerTags.length ? "Add another speaker…" : "Choose a speaker…"}
                  searchPlaceholder="Type a speaker's name…"
                  emptyLabel="No approved speaker matches that name"
                  value={null}
                  onChange={next => assignEventSpeaker.mutate({
                    speakerId: Number(next),
                    eventSlug: event.slug,
                  })}
                  options={speakerChoices}
                />
                <span style={crmHelperTextStyle}>
                  Selected speakers appear as tags. Hover or focus a tag to remove it.
                </span>
              </div>
                </div>
              )}
              {editTab === "content" && (
                <div
                  role="tabpanel"
                  id="event-edit-panel-content"
                  aria-labelledby="event-edit-tab-content"
                  style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", alignItems: "start" }}
                >
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Description</label>
                <textarea style={{ ...inputStyle, resize: "vertical" }} rows={4} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
              </div>
              {/*
                Highlights and agenda, both optional.
                
                Edited as plain lines of text rather than a repeater with add and
                remove buttons: these are short lists that are usually pasted or
                rewritten wholesale, and a row of controls per bullet is more
                interface than the content deserves. Empty lines are dropped, so
                the way to remove one is to delete it.
              */}
              <div>
                <label style={labelStyle} htmlFor="event-highlights">Highlights <span style={{ color: "#64748B", fontWeight: 500 }}>— optional, one per line</span></label>
                <textarea
                  id="event-highlights"
                  style={{ ...inputStyle, resize: "vertical" }}
                  rows={4}
                  value={(draft.highlights ?? []).join("\n")}
                  placeholder={"A practical framework you can use on Monday\nCandid Q&A with the speaker"}
                  onChange={e => setDraft(d => ({
                    ...d,
                    highlights: e.target.value.split("\n").map(line => line.trim()).filter(Boolean),
                  }))}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="event-agenda">Agenda <span style={{ color: "#64748B", fontWeight: 500 }}>— optional, one per line as “time — what happens”</span></label>
                <textarea
                  id="event-agenda"
                  style={{ ...inputStyle, resize: "vertical" }}
                  rows={4}
                  value={(draft.agenda ?? []).map(entry => `${entry.time} — ${entry.item}`).join("\n")}
                  placeholder={"6:00 PM — Doors and networking\n6:30 PM — Opening remarks"}
                  onChange={e => setDraft(d => ({
                    ...d,
                    agenda: e.target.value
                      .split("\n")
                      .map(line => {
                        // Em dash, en dash or a plain hyphen, because whichever one
                        // an admin types should work.
                        const [time, ...rest] = line.split(/\s+[—–-]\s+/);
                        const item = rest.join(" — ").trim();
                        return { time: time.trim(), item };
                      })
                      .filter(entry => entry.time && entry.item),
                  }))}
                />
              </div>
                </div>
              )}
              {editTab === "media" && (
                <div
                  role="tabpanel"
                  id="event-edit-panel-media"
                  aria-labelledby="event-edit-tab-media"
                  style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", alignItems: "start" }}
                >
              {/*
                Event art. Only for managed events: a catalog event has no database
                row to attach an image to, and its slug would key an object nothing
                ever reads.

                Uploading and choosing the hero image are separate steps on purpose.
                An admin cropping Facebook, Instagram and print versions of one
                design should not have each upload silently overwrite the event's
                hero — so the upload returns a URL, and the hero field below is
                where one of them is chosen.
              */}
              {event.isManaged && (
                <div style={{ gridColumn: "1 / -1" }}>
                  {(() => {
                    const currentArt = eventFeaturedArtwork({
                      slug: event.slug,
                      title: draft.title || event.title,
                      heroImage: draft.heroImage,
                      generatedImage: currentGraphic?.publicUrl,
                    });
                    const currentIsManual = currentArt.url.includes(`/app-storage/event-art/${event.slug}/`);
                    return (
                      <section aria-labelledby="event-detail-hero-background-heading" style={{ display: "grid", gap: ".8rem", padding: "1rem", border: "1px solid rgba(125,234,255,.18)", borderRadius: ".8rem", background: "rgba(125,234,255,.035)" }}>
                        <div>
                          <p id="event-detail-hero-background-heading" style={{ ...labelStyle, margin: 0 }}>Event detail hero background</p>
                          <p style={{ margin: ".3rem 0 0", color: "#8390A6", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>This landscape image appears behind the hero on the public Event Details page.</p>
                        </div>
                        <div className="group relative" style={{ overflow: "hidden", borderRadius: ".65rem", border: "1px solid rgba(255,255,255,.14)", background: "#080C18" }}>
                          <ResponsiveImage src={currentArt.url} alt="Current event detail hero background" sizes="640px" style={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "cover" }} />
                          <span style={{ position: "absolute", left: ".65rem", bottom: ".6rem", padding: ".25rem .45rem", borderRadius: ".35rem", background: "rgba(8,12,24,.86)", color: currentArt.isPlaceholder ? "#FCD34D" : "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{currentArt.isPlaceholder ? "Gradient fallback" : "Current background"}</span>
                          {currentIsManual && (
                            <button type="button" className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full border border-red-300/50 bg-red-950/90 text-red-100 opacity-0 shadow-lg transition-opacity group-hover:opacity-100 focus-visible:opacity-100" aria-label="Delete current event detail hero background" title="Delete uploaded image" disabled={deleteUploadedArt.isPending} onClick={() => deleteUploadedArt.mutate({ eventSlug: event.slug, url: currentArt.url })}>
                              {deleteUploadedArt.isPending ? <Loader2 size={16} className="animate-spin" /> : <X size={17} aria-hidden="true" />}
                            </button>
                          )}
                        </div>
                        <ImageUploader
                          label="Upload event detail hero background"
                          description="Choose one landscape image. It becomes the current Event Details hero background immediately and is saved with the event."
                          presetKeys={["facebook_wall"]}
                          defaultPresetKey="facebook_wall"
                          maxFiles={1}
                          onUpload={async (dataUrl, presetKey) => {
                            const stored = await uploadEventArt.mutateAsync({ slug: event.slug, presetKey, dataUrl });
                            setUploadedArt(current => [...current, { url: stored.url, presetKey }]);
                            setDraft(current => ({ ...current, heroImage: stored.url }));
                          }}
                        />
                      </section>
                    );
                  })()}

                  <div style={{ marginTop: "1rem" }}>
                  <ImageUploader
                    label="Additional event images"
                    description="Upload supporting artwork for social posts or alternate formats. Select an image below if you want it to become the Event Details hero background."
                    onUpload={async (dataUrl, presetKey) => {
                      const stored = await uploadEventArt.mutateAsync({ slug: event.slug, presetKey, dataUrl });
                      setUploadedArt(current => [...current, { url: stored.url, presetKey }]);
                    }}
                  />
                  </div>

                  <div style={{ marginTop: "1rem" }}>
                    <ImageUploader
                      label="Partner logo for generated hero"
                      description="Optional. Upload the real partner mark; the generator will composite these pixels and will never redraw or invent a logo."
                      presetKeys={["free"]}
                      defaultPresetKey="free"
                      maxFiles={1}
                      onUpload={async dataUrl => {
                        const stored = await uploadEventLogo.mutateAsync({ slug: event.slug, dataUrl });
                        setDraft(current => ({ ...current, partnerLogo: stored.url }));
                      }}
                    />
                    {draft.partnerLogo && (
                      <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginTop: ".7rem" }}>
                        <img src={draft.partnerLogo} alt="Current partner logo" style={{ width: 120, height: 54, objectFit: "contain", borderRadius: ".4rem", background: "rgba(255,255,255,.05)" }} />
                        <button type="button" onClick={() => setDraft(current => ({ ...current, partnerLogo: undefined }))} style={{ minHeight: 34, padding: "0 .6rem", border: "1px solid rgba(255,255,255,.14)", borderRadius: ".45rem", background: "transparent", color: "#CBD5E1", cursor: "pointer" }}>Remove logo</button>
                      </div>
                    )}
                  </div>

                  {/*
                    What the event looks like right now, art or not.
                    
                    An event with nothing uploaded used to show no image at all
                    here, so there was no way to tell from this screen whether the
                    art was missing or simply had not loaded — and no sense of
                    what the event currently presents as elsewhere on the site.
                  */}
                  <div style={{ marginTop: ".9rem" }}>
                    <label style={labelStyle}>AI-generated images</label>
                    {drawerGraphicsQuery.isLoading && !currentGraphic ? (
                      <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Loading generated images…</p>
                    ) : drawerGraphicsQuery.isError ? (
                      <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Generated images could not be loaded. Try reopening this event.</p>
                    ) : (currentGraphic?.variants?.length ?? 0) > 0 ? (
                      <>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: ".65rem" }}>
                        {currentGraphic!.variants.map(variant => {
                          const selected = draft.heroImage === variant.url;
                          const deleting = deleteGeneratedArt.isPending && deleteGeneratedArt.variables?.id === variant.id;
                          return (
                            <div key={variant.id} className="group relative" style={{ width: 132 }}>
                              <button
                                type="button"
                                onClick={() => setDraft(current => ({ ...current, heroImage: selected ? undefined : variant.url }))}
                                aria-pressed={selected}
                                title={selected ? "Selected as the Event Picture" : "Use this generated image as the Event Picture"}
                                style={{ width: "100%", padding: ".25rem", borderRadius: ".55rem", border: `2px solid ${selected ? "#E91E8C" : "rgba(255,255,255,.14)"}`, background: "rgba(7,12,24,.72)", cursor: "pointer" }}
                              >
                                <ResponsiveImage src={variant.url} alt="" sizes="124px" style={{ display: "block", width: "100%", height: 74, objectFit: "cover", borderRadius: ".35rem" }} />
                                <span style={{ display: "block", marginTop: ".25rem", color: selected ? "#F9A8D4" : "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                                  {selected ? "Selected" : "AI generated"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full border border-red-300/40 bg-red-950/90 text-red-100 opacity-0 shadow-lg transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                                aria-label={`Delete generated image from ${new Date(variant.generatedAt).toLocaleDateString()}`}
                                title="Delete generated image"
                                disabled={deleting}
                                onClick={() => deleteGeneratedArt.mutate({ id: variant.id, eventSlug: event.slug })}
                              >
                                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} aria-hidden="true" />}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <p style={{ margin: ".45rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                        Select an image to use it as the Event Picture, or hover over one to remove it.
                      </p>
                      </>
                    ) : (
                      <p style={{ margin: 0, color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                        {administratorEventArtwork(draft.heroImage)
                          ? "Event Picture already exists. Artwork generation is skipped."
                          : "No AI-generated images yet. Generate event artwork to create options here."}
                      </p>
                    )}
                  </div>

                  {uploadedArt.length > 0 && (
                    <div style={{ marginTop: ".9rem" }}>
                      <label style={labelStyle}>Event Picture</label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
                        {uploadedArt.map(art => {
                          const selected = draft.heroImage === art.url;
                          const deleting = deleteUploadedArt.isPending && deleteUploadedArt.variables?.url === art.url;
                          return (
                            <div key={art.url} className="group relative">
                              <button type="button" onClick={() => setDraft(d => ({ ...d, heroImage: selected ? undefined : art.url }))} aria-pressed={selected} title={selected ? "Selected as the hero background" : "Use this as the hero background"} style={{ padding: ".25rem", borderRadius: ".55rem", border: `2px solid ${selected ? "#E91E8C" : "rgba(255,255,255,.14)"}`, background: "transparent", cursor: "pointer" }}>
                                <ResponsiveImage src={art.url} alt="" sizes="96px" style={{ display: "block", width: 96, height: 60, objectFit: "cover", borderRadius: ".35rem" }} />
                                <span style={{ display: "block", marginTop: ".2rem", color: selected ? "#F9A8D4" : "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{IMAGE_PRESETS_BY_KEY[art.presetKey].label}</span>
                              </button>
                              <button type="button" className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded-full border border-red-300/50 bg-red-950/90 text-red-100 opacity-0 shadow-lg transition-opacity group-hover:opacity-100 focus-visible:opacity-100" aria-label={`Delete uploaded ${IMAGE_PRESETS_BY_KEY[art.presetKey].label} image`} title="Delete uploaded image" disabled={deleting} onClick={() => deleteUploadedArt.mutate({ eventSlug: event.slug, url: art.url })}>
                                {deleting ? <Loader2 size={15} className="animate-spin" /> : <X size={16} aria-hidden="true" />}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <p style={{ margin: ".4rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                        The Event Picture is saved with the rest of the event when you save changes.
                      </p>
                      {draft.heroImage && (
                        <button
                          type="button"
                          onClick={() => setDraft(d => ({ ...d, heroImage: undefined }))}
                          style={{ marginTop: ".65rem", minHeight: 34, padding: ".42rem .66rem", borderRadius: ".52rem", border: "1px solid rgba(255,255,255,.13)", background: "rgba(255,255,255,.03)", color: "#C7D0DF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                        >
                          Use gradient fallback instead
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {event.isManaged && (
                <section
                  aria-labelledby="event-video-heading"
                  style={{ gridColumn: "1 / -1", display: "grid", gap: ".8rem", padding: "1rem", borderRadius: ".75rem", border: "1px solid rgba(0,212,255,.16)", background: "rgba(0,212,255,.035)" }}
                >
                  <div>
                    <p id="event-video-heading" style={{ ...labelStyle, marginBottom: ".3rem" }}>Event recording</p>
                    <p style={{ margin: 0, color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
                      Paste a YouTube or Vimeo link, or choose a browser-playable video already stored in Replit App Storage.
                    </p>
                  </div>
                  <input
                    aria-label="YouTube, Vimeo, or managed video URL"
                    type="url"
                    style={inputStyle}
                    value={draft.videoUrl ?? ""}
                    placeholder="https://www.youtube.com/watch?v=…"
                    onChange={e => setDraft(current => ({ ...current, videoUrl: e.target.value || undefined }))}
                  />
                  {draft.videoUrl && !resolveEventVideoSource(draft.videoUrl) && (
                    <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                      Enter a valid YouTube or Vimeo link, or select a video below.
                    </p>
                  )}
                  {draft.videoUrl && resolveEventVideoSource(draft.videoUrl) && (
                    <div style={{ maxWidth: 620 }}>
                      <EventVideoPlayer url={draft.videoUrl} title={`${draft.title || event.title} video preview`} />
                      <button
                        type="button"
                        onClick={() => setDraft(current => ({ ...current, videoUrl: undefined }))}
                        style={{ marginTop: ".55rem", minHeight: 34, padding: "0 .65rem", borderRadius: ".45rem", border: "1px solid rgba(248,113,113,.3)", background: "rgba(248,113,113,.07)", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                      >
                        Remove video
                      </button>
                    </div>
                  )}
                  <div>
                    <p style={{ ...labelStyle, marginBottom: ".45rem" }}>Browse managed videos</p>
                    {videoLibraryQuery.isLoading ? (
                      <p style={{ margin: 0, color: "#94A3B8", fontSize: "var(--chi-body-compact)" }}>Loading the video library…</p>
                    ) : videoLibraryQuery.isError ? (
                      <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontSize: "var(--chi-body-compact)" }}>{videoLibraryQuery.error.message}</p>
                    ) : (videoLibraryQuery.data?.length ?? 0) === 0 ? (
                      <p style={{ margin: 0, color: "#718096", fontSize: "var(--chi-body-compact)" }}>No public MP4, WebM, OGV, OGG, or M4V videos were found in App Storage.</p>
                    ) : (
                      <div className="event-video-library">
                        {videoLibraryQuery.data?.map(video => {
                          const selected = draft.videoUrl === video.url;
                          return (
                            <button
                              key={video.key}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => setDraft(current => ({ ...current, videoUrl: video.url }))}
                              className="event-video-library__item"
                              data-selected={selected || undefined}
                            >
                              <Video size={16} aria-hidden="true" />
                              <span title={video.key}>{video.name}</span>
                              <small>{video.contentType.replace("video/", "").toUpperCase()}</small>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
              )}
              {event.isManaged && <EventDocumentsEditor eventSlug={event.slug} />}
              {event.isManaged && <EventResourceManager eventSlug={event.slug} />}
                </div>
              )}
              <EventEditStepControls tabs={EVENT_EDIT_TABS} activeId={editTab} onChange={id => setEditTab(id as EventEditTabId)} />
            </div>
          ) : (
            /* ── VIEW MODE ── */
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {/* Meta grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                {[
                  { label: "Date", value: event.displayDate },
                  { label: "Time", value: event.time },
                  // "Dallas, TX" rather than the stored "The Board Studios, Dallas, TX":
                  // view mode answers where, and the venue is detail the drawer has
                  // room for elsewhere. A location with no city shows its own text.
                  { label: "Location", value: formatEventLocationDisplay(event.location) },
                  { label: "Speaker", value: `${event.speaker} · ${event.speakerOrg}` },
                  /*
                    One capacity row, not two. "Seats Remaining" was the same
                    numbers subtracted — 12 / 40 registered and 28 available say
                    the same thing twice, and two tiles for one fact is two things
                    to read before you know what you already knew.
                  */
                  { label: "Capacity", value: `${event.registered} of ${event.seats} registered · ${getEventSeatsRemaining(event)} left` },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.75rem 1rem" }}>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.25rem" }}>{label}</p>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "white", fontWeight: 500 }}>{value}</p>
                  </div>
                ))}
              </div>

              {event.type === "virtual" && (
                <section aria-label="Zoom meeting link" style={{ background: "rgba(0,212,255,0.045)", border: "1px solid rgba(0,212,255,0.18)", borderRadius: "0.625rem", padding: "0.9rem 1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".8rem", marginBottom: ".65rem" }}>
                    <div>
                      <p style={{ margin: 0, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#72E6F6", letterSpacing: "0.07em", textTransform: "uppercase" }}>Zoom attendee link</p>
                      <p style={{ margin: ".22rem 0 0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#94A3B8", lineHeight: 1.45 }}>Only registered, signed-in attendees can retrieve this link. Do not paste a Zoom host start URL.</p>
                    </div>
                    {/*
                      An icon, not "Open ↗".
                      
                      The word plus a text arrow wrapped onto two lines in the
                      corner of this panel and read as a stray glyph. The action
                      is obvious from an external-link icon, and the accessible
                      name and tooltip still say it in words.
                    */}
                    {zoomMeetingQuery.data?.joinUrl && (
                      <a
                        href={zoomMeetingQuery.data.joinUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open the Zoom meeting link in a new tab"
                        title="Open the Zoom meeting link in a new tab"
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", width: 30, height: 30, borderRadius: ".45rem", border: "1px solid rgba(0,212,255,.28)", background: "rgba(0,212,255,.1)", color: "#76E9F8" }}
                      >
                        <ArrowUpRight size={15} aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  {/*
                    Nothing is shown until a route is chosen. Both controls used to sit on screen
                    together — a generate button beside a paste-a-link field — which asked the
                    administrator to work out which of two ways applied before reading either.

                    "Create New" is the generate action itself rather than a step towards it, so
                    choosing it books the meeting in Zoom immediately. Only "Add Existing" has a
                    second step, because it needs the link pasted in.
                  */}
                  {zoomMode === "existing" ? (
                    <div style={{ display: "grid", gap: ".55rem" }}>
                      <button
                        type="button"
                        onClick={() => setZoomMode("idle")}
                        style={{ justifySelf: "start", display: "inline-flex", alignItems: "center", gap: ".3rem", padding: 0, border: 0, background: "transparent", color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                      >
                        <ArrowLeft size={13} aria-hidden="true" /> Back
                      </button>
                      <div style={{ display: "flex", gap: ".55rem", flexWrap: "wrap" }}>
                        <input aria-label="Zoom attendee link" type="url" value={zoomJoinUrl} onChange={e => setZoomJoinUrl(e.target.value)} placeholder="Enter the full Zoom join link" style={{ ...inputStyle, flex: "1 1 290px" }} />
                        <button type="button" disabled={!zoomJoinUrl.trim() || saveZoomMeeting.isPending} onClick={() => saveZoomMeeting.mutate({ eventSlug: event.slug, joinUrl: zoomJoinUrl })} style={{ padding: ".55rem .8rem", border: 0, borderRadius: ".5rem", background: "#0E7490", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: saveZoomMeeting.isPending ? "wait" : "pointer", opacity: saveZoomMeeting.isPending ? .7 : 1 }}>{saveZoomMeeting.isPending ? "Saving…" : "Save link"}</button>
                        {zoomMeetingQuery.data?.joinUrl && <button type="button" disabled={removeZoomMeeting.isPending} onClick={() => setConfirmRemoveZoom(true)} style={{ padding: ".55rem .8rem", border: "1px solid rgba(248,113,113,.35)", borderRadius: ".5rem", background: "rgba(248,113,113,.08)", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: removeZoomMeeting.isPending ? "wait" : "pointer" }}>{removeZoomMeeting.isPending ? "Removing…" : "Remove"}</button>}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: ".5rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".55rem", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => setZoomMode("existing")}
                          style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".55rem .8rem", border: "1px solid rgba(0,212,255,.3)", borderRadius: ".5rem", background: "rgba(0,212,255,.08)", color: "#76E9F8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                        >
                          <LinkIcon size={14} aria-hidden="true" /> Add Existing
                        </button>
                        <button
                          type="button"
                          disabled={createZoomMeeting.isPending || zoomMeetingQuery.data?.source === "zoom_api"}
                          onClick={() => createZoomMeeting.mutate({ eventSlug: event.slug })}
                          style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".55rem .8rem", border: 0, borderRadius: ".5rem", background: zoomMeetingQuery.data?.source === "zoom_api" ? "rgba(34,197,94,.16)" : "#0369A1", color: zoomMeetingQuery.data?.source === "zoom_api" ? "#86EFAC" : "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: createZoomMeeting.isPending || zoomMeetingQuery.data?.source === "zoom_api" ? "default" : "pointer", opacity: createZoomMeeting.isPending ? .7 : 1 }}
                        >
                          <Plus size={14} aria-hidden="true" />
                          {createZoomMeeting.isPending ? "Creating…" : zoomMeetingQuery.data?.source === "zoom_api" ? "Zoom meeting ready" : "Create New"}
                        </button>
                        {zoomMeetingQuery.data?.joinUrl && <button type="button" disabled={removeZoomMeeting.isPending} onClick={() => setConfirmRemoveZoom(true)} style={{ padding: ".55rem .8rem", border: "1px solid rgba(248,113,113,.35)", borderRadius: ".5rem", background: "rgba(248,113,113,.08)", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: removeZoomMeeting.isPending ? "wait" : "pointer" }}>{removeZoomMeeting.isPending ? "Removing…" : "Remove"}</button>}
                      </div>
                      <span style={{ color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.4 }}>Create New books the meeting in Zoom using this event’s saved date, time, and timezone. A generated attendee link remains gated to registered members.</span>
                      {/*
                        The saved link, read back and ready to copy.
                        `zoomJoinUrl` is kept in step with the stored meeting by the
                        effect above, so this shows what Zoom returned rather than
                        anything retyped here. Read-only with select-on-focus: the
                        value is for copying out, and an editable box invites a stray
                        keystroke into a link attendees depend on.
                      */}
                      {zoomMeetingQuery.data?.joinUrl && (
                        <div style={{ marginTop: ".75rem", display: "grid", gap: ".3rem" }}>
                          <label htmlFor="event-zoom-saved-link" style={{ color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                            Attendee link{zoomMeetingQuery.data.joinUrl.includes("simulated=1") ? " — simulated for local development" : ""}
                          </label>
                          <div style={{ display: "flex", gap: ".55rem", flexWrap: "wrap" }}>
                            <input
                              id="event-zoom-saved-link"
                              readOnly
                              value={zoomMeetingQuery.data.joinUrl}
                              onFocus={e => e.currentTarget.select()}
                              style={{ ...inputStyle, flex: "1 1 290px" }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard?.writeText(zoomMeetingQuery.data!.joinUrl)
                                  .then(() => chiNotify.success("Attendee link copied"))
                                  .catch(() => chiNotify.error("The link could not be copied", { description: "Select the field and copy it manually." }));
                              }}
                              style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", padding: ".55rem .8rem", border: "1px solid rgba(0,212,255,.28)", borderRadius: ".5rem", background: "rgba(0,212,255,.08)", color: "#76E9F8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                            >
                              <Copy size={14} aria-hidden="true" /> Copy
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* Fill rate bar */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.875rem 1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase" }}>Fill Rate</span>
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: fill >= 80 ? "#E91E8C" : fill >= 50 ? "#F5A623" : "#22C55E" }}>{fill}%</span>
                </div>
                <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${fill}%`, background: fill >= 80 ? "linear-gradient(90deg,#E91E8C,#C2185B)" : fill >= 50 ? "linear-gradient(90deg,#F5A623,#E08A00)" : "linear-gradient(90deg,#22C55E,#16A34A)", borderRadius: 3, transition: "width 0.4s ease" }} />
                </div>
              </div>

              {/* Description */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.875rem 1rem" }}>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.5rem" }}>Description</p>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#CBD5E1", lineHeight: 1.7 }}>{event.description}</p>
              </div>

              {/* Highlights */}
              {event.highlights?.length > 0 && (
                <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.875rem 1rem" }}>
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.625rem" }}>Highlights</p>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                    {event.highlights.map((h, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#CBD5E1" }}>
                        <CheckCircle size={13} style={{ color: event.color, flexShrink: 0, marginTop: 2 }} /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Agenda */}
              {event.agenda && event.agenda.length > 0 && (
                <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.875rem 1rem" }}>
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "0.625rem" }}>Agenda</p>
                  {event.agenda.map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: "0.75rem", padding: "0.4rem 0", borderBottom: i < event.agenda!.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, color: event.color, minWidth: 70 }}>{item.time}</span>
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#CBD5E1" }}>{item.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {event.isManaged && event.specialEventApproval && (
            <div style={{ marginTop: "1.1rem" }}>
              <SpecialEventApprovalControl
                approval={event.specialEventApproval}
                value={specialEventDraft}
                onChange={setSpecialEventDraft}
                pricingMode={savedPricingMode}
                readOnly={!isEditing}
              />
            </div>
          )}

          <div style={{ marginTop: "1.1rem" }}>
            <EventRegistrationTypeControl
              eventSlug={event.slug}
              readOnly={!isEditing}
              isSpecialEvent={event.specialEventApproval?.isSpecialEvent ?? false}
            />
          </div>

          {/* Footer actions */}
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <button
              onClick={() => { onViewAttendees(event.id); onClose(); }}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "0.7rem", background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)", borderRadius: "0.625rem", color: "#00D4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,212,255,0.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,212,255,0.08)"; }}
            >
              <Users size={14} /> View Attendees
            </button>
            <button
              onClick={() => { chiNotify.info("Export event data — coming in next release"); }}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "0.7rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.625rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
            >
              <Download size={14} /> Export Data
            </button>
          </div>
          </div>

          {/*
            Archive sits hard left, apart from the pair on the right.

            It is the one action here that takes the event off the site, so it is
            deliberately not adjacent to Save — a destructive control beside the
            one people click without looking is how it gets clicked without
            looking. Only a managed event can be archived; a code-defined catalog
            event has no database row to archive.
          */}
          <div className="crm-drawer__footer" style={{ justifyContent: event.isManaged ? "space-between" : undefined }}>
            {event.isManaged && (
              <button
                type="button"
                onClick={() => setConfirmArchive(true)}
                disabled={archiveFromDrawer.isPending}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", minHeight: 38, padding: "0 .85rem", borderRadius: ".55rem", border: "1px solid rgba(248,113,113,.4)", background: "rgba(248,113,113,.1)", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: archiveFromDrawer.isPending ? "wait" : "pointer", marginRight: "auto" }}
              >
                <Archive size={13} /> Archive
              </button>
            )}
            <div style={{ display: "inline-flex", gap: ".5rem" }}>
            {isEditing ? (
              <>
                <button type="button" className="crm-drawer__secondary" onClick={handleDiscard} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                  <RotateCcw size={13} /> Discard
                </button>
                <button type="button" className="crm-drawer__primary" disabled={updateEvent.isPending} onClick={handleSave} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", background: "linear-gradient(135deg,#22C55E,#16A34A)", borderColor: "rgba(34,197,94,.45)" }}>
                  <Save size={13} /> {updateEvent.isPending ? "Saving…" : "Save changes"}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="crm-drawer__secondary" onClick={onClose}>Close</button>
                {event.isManaged && (
                  <button type="button" className="crm-drawer__primary" onClick={() => setIsEditing(true)} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                    <Edit2 size={13} /> Edit event
                  </button>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </SheetContent>

      {/*
        Confirmed, because it is not undoable from the event itself and it has a
        consequence beyond this record: people are registered for this. The count
        is read from the event so the warning is about *these* attendees rather
        than a generic caution nobody reads twice.
      */}
      <AlertDialog open={confirmArchive} onOpenChange={open => { if (!open) setConfirmArchive(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to archive this event?</AlertDialogTitle>
            <AlertDialogDescription>
              <span style={{ display: "block" }}>
                Archiving <strong style={{ color: "#F8FAFC" }}>{event.title}</strong> removes it from your website. It will no longer be
                available to any attendees currently registered.
              </span>
              {event.registered > 0 && (
                <span style={{ display: "block", marginTop: ".6rem", color: "#FCD34D" }}>
                  {event.registered} {event.registered === 1 ? "person is" : "people are"} registered for this event.
                </span>
              )}
              <span style={{ display: "block", marginTop: ".6rem" }}>
                Nothing is deleted — the event and its registrations are kept, and it moves to the Archived tab.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the event</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveFromDrawer.mutate({ slug: event.slug })}
              disabled={archiveFromDrawer.isPending}
            >
              {archiveFromDrawer.isPending ? "Archiving…" : "Archive event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/*
        Confirmed because the consequence reaches outside this record: for a meeting
        this workspace booked, removing the link deletes it in Zoom. The wording
        changes with what is actually about to happen — a pasted link is only
        detached, and a simulated one never existed in Zoom at all.
      */}
      <AlertDialog open={confirmRemoveZoom} onOpenChange={open => { if (!open) setConfirmRemoveZoom(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {zoomMeetingIsOurs ? "Delete this meeting from Zoom?" : "Remove this attendee link?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {zoomMeetingIsOurs ? (
                <>
                  <span style={{ display: "block" }}>
                    This deletes the Zoom meeting for <strong style={{ color: "#F8FAFC" }}>{event.title}</strong>, not just its link
                    here. Anyone holding the link will no longer be able to join, and the meeting disappears from the host's Zoom calendar.
                  </span>
                  {event.registered > 0 && (
                    <span style={{ display: "block", marginTop: ".6rem", color: "#FCD34D" }}>
                      {event.registered} {event.registered === 1 ? "person is" : "people are"} registered and will have no way to join
                      until a new meeting is attached.
                    </span>
                  )}
                  <span style={{ display: "block", marginTop: ".6rem" }}>
                    Registrations are kept. This cannot be undone from Zoom — a new meeting would have to be created.
                  </span>
                </>
              ) : (
                <>
                  <span style={{ display: "block" }}>
                    This detaches the attendee link from <strong style={{ color: "#F8FAFC" }}>{event.title}</strong>. Registered
                    attendees will have no link until another is attached.
                  </span>
                  <span style={{ display: "block", marginTop: ".6rem" }}>
                    {zoomMeetingIsSimulated
                      ? "This link is a local development simulation, so there is no Zoom meeting to delete."
                      : "The meeting was created outside this workspace, so it stays in Zoom untouched."}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the link</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeZoomMeeting.mutate({ eventSlug: event.slug })}
              disabled={removeZoomMeeting.isPending}
            >
              {removeZoomMeeting.isPending
                ? "Removing…"
                : zoomMeetingIsOurs ? "Delete in Zoom" : "Remove link"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

type EventRecordActionProps = {
  event: AdminEvent;
  onOpen: () => void;
  onViewAttendees: () => void;
  onEdit: () => void;
  onLinkZoom: () => void;
  onArchive: () => void;
  onDelete: () => void;
};

/**
 * A Zoom meeting can only be attached to a managed virtual event: the link is
 * stored against the database record, and an in-person event has nothing to join.
 */
function canLinkZoom(event: AdminEvent) {
  return event.isManaged && event.type === "virtual";
}

function EventRecordContextMenu({ event, onOpen, onViewAttendees, onEdit, onLinkZoom, onArchive, onDelete, children }: EventRecordActionProps & { children: ReactNode }) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuLabel>{event.isManaged ? "Managed event actions" : "Published catalog event"}</ContextMenuLabel>
        <ContextMenuItem onSelect={onOpen}><Eye size={15} /> View event details</ContextMenuItem>
        <ContextMenuItem onSelect={onViewAttendees}><Users size={15} /> View attendees</ContextMenuItem>
        <ContextMenuSeparator />
        {event.isManaged ? (
          <>
            <ContextMenuItem onSelect={onEdit}><FilePenLine size={15} /> Edit event</ContextMenuItem>
            {canLinkZoom(event) && <ContextMenuItem onSelect={onLinkZoom}><Video size={15} /> Link a Zoom meeting</ContextMenuItem>}
            <ContextMenuItem onSelect={onArchive}><Archive size={15} /> Archive event</ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem variant="destructive" onSelect={onDelete}><Trash2 size={15} /> Delete empty event</ContextMenuItem>
          </>
        ) : (
          <ContextMenuItem disabled>Published catalog events are read-only</ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function EventRecordActionDropdown({ event, onOpen, onViewAttendees, onEdit, onLinkZoom, onArchive, onDelete }: EventRecordActionProps) {
  /*
   * The shared CRM row menu, so this table matches members and speakers: one
   * "Actions" trigger with a chevron, a portalled panel that cannot be clipped
   * by the table's scroll container, and no name header — the row already says
   * whose actions these are.
   *
   * A published catalog event is read-only, so it gets the two view actions and
   * nothing that would imply an edit it cannot accept.
   */
  const actions: DataTableAction[] = [
    { key: "open", label: "View event details", icon: <Eye size={13} aria-hidden="true" />, onSelect: onOpen },
    { key: "attendees", label: "View attendees", icon: <Users size={13} aria-hidden="true" />, onSelect: onViewAttendees },
    ...(event.isManaged
      ? [
          { key: "edit", label: "Edit event", icon: <FilePenLine size={13} aria-hidden="true" />, onSelect: onEdit },
          ...(canLinkZoom(event)
            ? [{ key: "zoom", label: "Link a Zoom meeting", icon: <Video size={13} aria-hidden="true" />, onSelect: onLinkZoom }]
            : []),
          { key: "archive", label: "Archive event", icon: <Archive size={13} aria-hidden="true" />, onSelect: onArchive },
          { key: "delete", label: "Delete empty event", icon: <Trash2 size={13} aria-hidden="true" />, tone: "danger" as const, onSelect: onDelete },
        ]
      : []),
  ];

  return <DataTableActions subject={event.title} actions={actions} />;
}

// ── Events Table View ──────────────────────────────────────────────────────────
function EventsView({ onSelectEvent, onPreviewEvent, onViewRoster, onComposeEventUpdate }: { onSelectEvent: (id: number) => void; onPreviewEvent: (event: AdminEvent, startInEditMode?: boolean) => void; onViewRoster: (event: AdminEvent) => void; onComposeEventUpdate?: () => void }) {
  const eventClock = useEventClock();
  const [search, setSearch] = useState("");
  /*
   * Seeded from the URL, so "View all events" on a speaker arrives here already
   * narrowed to them. Read once on mount rather than watched: after landing, the
   * filter is the administrator's to change, and re-reading the URL would keep
   * dragging it back to the speaker they came from.
   */
  const [speakerFilter, setSpeakerFilter] = useState(
    () => new URLSearchParams(window.location.search).get("speaker") ?? "all",
  );
  /*
   * Upcoming by default. The table is a working list — what is coming — and the
   * past is a reference you go looking for, so it should not be mixed into the
   * default view where it grows without limit.
   */
  const [timeframe, setTimeframe] = useState<"all" | "upcoming" | "past">("upcoming");
  /** Virtual or in-person, narrowing whichever timeframe is showing. */
  const [typeFilter, setTypeFilter] = useState<"all" | "virtual" | "in-person">("all");
  const [createOpen, setCreateOpen] = useState(() => new URLSearchParams(window.location.search).get("createEvent") === "1");
  const [pendingAction, setPendingAction] = useState<{ kind: "archive" | "delete"; event: AdminEvent } | null>(null);
  const [zoomLinkEvent, setZoomLinkEvent] = useState<AdminEvent | null>(null);
  const managedEventsQuery = trpc.events.managedEvents.useQuery(undefined, {
    retry: false,
    refetchInterval: paidEventCatalogRefetchInterval,
    refetchOnWindowFocus: true,
  });
  const adminGraphicsQuery = trpc.events.adminGraphics.useQuery(undefined, { retry: false });
  /**
   * Which population the table is showing.
   *
   * Archiving used to hide an event from every list including this one, so an
   * event archived by mistake could not be found again from the interface at all.
   * The archived list is fetched only when it is being looked at — most sessions
   * never open it.
   */
  const [population, setPopulation] = useState<"active" | "archived">("active");
  const archivedEventsQuery = trpc.events.archivedEvents.useQuery(undefined, {
    retry: false,
    enabled: population === "archived",
  });
  // Real attendee counts, so the table stops presenting eventsData.ts literals as
  // registration numbers. Tolerates failure: an unavailable count shows 0, which
  // is honest, rather than falling back to a fabricated figure.
  const registrationCountsQuery = trpc.events.adminRegistrationCounts.useQuery(undefined, {
    retry: false,
    refetchInterval: query => (typeof document === "undefined" || document.visibilityState !== "hidden" ? 15_000 : false),
    refetchOnWindowFocus: true,
  });
  const speakersByEventSlug = useAdminEventSpeakerPortraits();
  const utils = trpc.useUtils();
  const invalidateEventLists = async () => {
    await Promise.all([
      utils.events.managedEvents.invalidate(),
      utils.events.archivedEvents.invalidate(),
      utils.events.publicCatalog.invalidate(),
      utils.events.pricingPolicies.invalidate(),
    ]);
  };
  const archiveEvent = trpc.events.archive.useMutation({
    onSuccess: async archived => {
      await invalidateEventLists();
      setPendingAction(null);
      chiNotify.success("Event archived", { description: `${archived.title} is no longer in the active event list.` });
    },
    onError: error => chiNotify.error("Event not archived", { description: error.message }),
  });
  const deleteEvent = trpc.events.delete.useMutation({
    onSuccess: async deleted => {
      await invalidateEventLists();
      setPendingAction(null);
      chiNotify.success("Empty event deleted", { description: `${deleted.title} was removed.` });
    },
    onError: error => chiNotify.error("Event not deleted", { description: error.message }),
  });
  const events = useMemo<AdminEvent[]>(() => {
    const counts = registrationCountsQuery.data ?? {};
    const graphicsBySlug = new Map(
      (adminGraphicsQuery.data ?? []).map(graphic => [graphic.eventSlug, graphic as AdminEventGraphic]),
    );

    /*
     * The archived list is only ever managed events. A code-defined event has no
     * database row to archive, so mixing the catalog into this view would list
     * events that cannot be in it.
     */
    if (population === "archived") {
      return ((archivedEventsQuery.data ?? []) as CHIEvent[]).map(event => ({
        ...event,
        registered: counts[event.slug] ?? 0,
        isManaged: true,
        graphic: graphicsBySlug.get(event.slug),
      }));
    }

    const managed = (managedEventsQuery.data ?? []).map(event => {
      const catalogEvent = EVENTS.find(candidate => candidate.slug === event.slug);
      const typedEvent = event as CHIEvent;
      return catalogEvent?.eventImage && !typedEvent.eventImage
        ? { ...event, eventImage: catalogEvent.eventImage }
        : event;
    }) as CHIEvent[];
    const managedSlugs = new Set(managed.map(event => event.slug));
    return [
      // `registered` is overridden for both sources from the same measured counts,
      // so a static event can never display a hardcoded figure and the two kinds
      // of row are directly comparable.
      ...EVENTS.filter(event => !managedSlugs.has(event.slug)).map(event => ({
        ...event,
        registered: counts[event.slug] ?? 0,
        isManaged: false,
        graphic: graphicsBySlug.get(event.slug),
      })),
      ...managed.map(event => ({ ...event, registered: counts[event.slug] ?? 0, isManaged: true, graphic: graphicsBySlug.get(event.slug) })),
    ];
  }, [adminGraphicsQuery.data, archivedEventsQuery.data, managedEventsQuery.data, population, registrationCountsQuery.data]);
  const unmigratedCount = events.filter(event => !event.isManaged).length;

  const migrateCanonicalEvents = trpc.events.migrateCanonicalEvents.useMutation({
    onSuccess: async result => {
      await Promise.all([
        utils.events.managedEvents.invalidate(),
        utils.events.publicCatalog.invalidate(),
      ]);
      // Failures are reported per event rather than as one outcome: a partial run is
      // recoverable by fixing the named event and running again.
      if (result.failed.length > 0) {
        chiNotify.error(`${result.failed.length} event(s) could not be migrated`, {
          description: result.failed.map(entry => `${entry.slug}: ${entry.reason}`).join(" · "),
        });
        return;
      }
      chiNotify.success(`${result.migrated.length} event(s) migrated`, {
        description: "They are now editable here, and registrations for them will include a calendar invite.",
      });
    },
    onError: error => chiNotify.error("Migration could not run", { description: error.message }),
  });

  const invalidateGraphicState = async () => {
    await Promise.all([
      utils.events.adminGraphics.invalidate(),
      utils.events.publicGraphics.invalidate(),
      utils.events.publicCatalog.invalidate(),
    ]);
  };
  const backfillGraphics = trpc.events.backfillGraphics.useMutation({
    onSuccess: async result => {
      await invalidateGraphicState();
      chiNotify.success("Event artwork queued", {
        description: `${result.queued} event graphic${result.queued === 1 ? "" : "s"} queued; generation continues in the background.`,
      });
    },
    onError: error => chiNotify.error("Artwork backfill could not run", { description: error.message }),
  });
  const retryGraphic = trpc.events.retryGraphic.useMutation({
    onSuccess: async () => {
      await invalidateGraphicState();
      chiNotify.success("Event artwork retry queued", { description: "The new graphic will appear when generation finishes." });
    },
    onError: error => chiNotify.error("Artwork retry could not run", { description: error.message }),
  });
  const retryHeroGraphic = trpc.events.retryHeroGraphic.useMutation({
    onSuccess: async () => {
      await invalidateGraphicState();
      chiNotify.success("Featured hero retry queued", {
        description: "The transparent 4K hero asset will appear when generation finishes.",
      });
    },
    onError: error => chiNotify.error("Featured hero retry could not run", { description: error.message }),
  });

  const totalAttendees = events.reduce((sum, e) => sum + e.registered, 0);
  const totalSeats = events.reduce((sum, e) => sum + e.seats, 0);
  const graphicCounts = useMemo(() => {
    const counts: Record<EventGraphicAdminStatus, number> = {
      not_requested: 0,
      processing: 0,
      ready: 0,
      failed: 0,
    };
    for (const event of events) counts[event.graphic?.status ?? "not_requested"] += 1;
    return counts;
  }, [events]);
  const heroGraphicCounts = useMemo(() => {
    const counts: Record<EventGraphicAdminStatus, number> = {
      not_requested: 0,
      processing: 0,
      ready: 0,
      failed: 0,
    };
    for (const event of events) counts[event.graphic?.heroGraphic?.status ?? "not_requested"] += 1;
    return counts;
  }, [events]);

  const speakerOptions = useMemo(
    () => Array.from(new Set(events.map(e => e.speaker).filter(Boolean))).sort((left, right) => left.localeCompare(right)),
    [events],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return events.filter(e => {
      const matchesTerm =
        term === "" || e.title.toLowerCase().includes(term) || e.location.toLowerCase().includes(term);
      const matchesSpeaker = speakerFilter === "all" || e.speaker === speakerFilter;
      const isPastEvent = isPastCalendarEvent(e, eventClock);
      const matchesTimeframe = timeframe === "all" ? true : timeframe === "upcoming" ? !isPastEvent : isPastEvent;
      const matchesType = typeFilter === "all" || e.type === typeFilter;
      return matchesTerm && matchesSpeaker && matchesTimeframe && matchesType;
    });
  }, [events, eventClock, search, speakerFilter, timeframe, typeFilter]);

  /* Counts come from the unfiltered list, so each tab says how many it holds
     rather than how many survive the current search. */
  const timeframeCounts = useMemo(() => {
    let upcoming = 0;
    let past = 0;
    for (const event of events) {
      if (isPastCalendarEvent(event, eventClock)) past += 1;
      else upcoming += 1;
    }
    return {
      upcoming,
      past,
      virtual: events.filter(event => event.type === "virtual").length,
      inPerson: events.filter(event => event.type === "in-person").length,
    };
  }, [events, eventClock]);

  return (
    <div className="events-management-view" style={{ padding: "2rem", flex: 1, overflowY: "auto" }}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.75rem", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.75rem", color: "white", letterSpacing: "0.04em", marginBottom: "0.25rem" }}>Events Management</h1>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B" }}>Right-click a managed event for actions, or use its actions button for keyboard access.</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" }}>
            <button
              onClick={() => setCreateOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.65rem 1.25rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", border: "none", borderRadius: "0.625rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 16px rgba(233,30,140,0.3)" }}
            >
              <Plus size={15} /> Create New Event
            </button>
            {/*
              Sends people to the composer rather than opening a second one here.
              Messages already owns drafting, consent and delivery; a separate
              path from this page would be a second implementation of all three.
            */}
            <button
              type="button"
              onClick={() => onComposeEventUpdate?.()}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.65rem 1.25rem", border: "1px solid rgba(125,234,255,.3)", borderRadius: "0.65rem", background: "rgba(125,234,255,.08)", color: "#9FF3FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
            >
              <MessageSquare size={15} /> Send Event Updates
            </button>
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          <ContextMenuLabel>Events workspace</ContextMenuLabel>
          <ContextMenuItem onSelect={() => setCreateOpen(true)}><Plus size={15} /> Create a new event</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/*
        Upcoming or Archived, before anything else on the page.

        These choose which set of events exists at all, so they sit above the
        filter tiles rather than among them: Virtual narrows a list, Archived
        replaces it. Archiving previously removed an event from every view
        including this one, which made a mis-archived event unrecoverable from
        the interface.
      */}
      {/*
        Three views, not two.

        The tabs were "Upcoming" and "Archived", and choosing "Upcoming" also
        narrowed the timeframe to upcoming — so a finished event that had not been
        archived belonged to no tab at all. It was reachable only by noticing the
        "Past" stat tile, which is where someone goes to attach a recording after
        the event has happened. Each tab is a view: which population, narrowed how.
      */}
      <div className="events-timeframe" role="tablist" aria-label="Show upcoming, past, or archived events" style={{ marginBottom: "1rem" }}>
        {([
          { key: "upcoming" as const, label: "Upcoming", population: "active" as const, timeframe: "upcoming" as const },
          { key: "past" as const, label: "Past", population: "active" as const, timeframe: "past" as const },
          { key: "archived" as const, label: "Archived", population: "archived" as const, timeframe: "all" as const },
        ]).map(tab => {
          /* Derived from the state the tiles also write, so the two never disagree. */
          const isActive = population === tab.population
            && (tab.population === "archived" || timeframe === tab.timeframe);
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-active={isActive || undefined}
              onClick={() => {
                setPopulation(tab.population);
                setTimeframe(tab.timeframe);
                setTypeFilter("all");
              }}
            >
              {tab.label}
              {tab.key === "past" ? <span>{timeframeCounts.past}</span> : null}
              {tab.key === "archived" && archivedEventsQuery.data
                ? <span>{archivedEventsQuery.data.length}</span>
                : null}
            </button>
          );
        })}
      </div>

      {/*
        The same filter tiles the Members and Speakers tables use.

        These replaced four read-only stat cards, two of which could not become
        filters: an average fill rate does not narrow a table, and a registration
        total counts people rather than rows. Both still exist on the Operations
        dashboard, which is where a figure you cannot act on belongs.

        Two axes again: Total/Upcoming/Past choose the set, Virtual/In-person
        narrow whichever set is showing.
      */}
      <CrmFilterTiles
        ariaLabel="Event filters"
        tiles={[
          { key: "all", label: "Total events", value: events.length, icon: CalendarDays,
            color: "#F9A8D4", background: "rgba(233,30,140,.10)", border: "rgba(233,30,140,.24)",
            selected: timeframe === "all" && typeFilter === "all",
            onSelect: () => { setTimeframe("all"); setTypeFilter("all"); } },
          { key: "upcoming", label: "Upcoming", value: timeframeCounts.upcoming, icon: Clock,
            color: "#F5A623", background: "rgba(245,166,35,.12)", border: "rgba(245,166,35,.26)",
            selected: timeframe === "upcoming",
            onSelect: () => setTimeframe("upcoming") },
          { key: "past", label: "Past", value: timeframeCounts.past, icon: Archive,
            color: "#94A3B8", background: "rgba(148,163,184,.12)", border: "rgba(148,163,184,.26)",
            selected: timeframe === "past",
            onSelect: () => setTimeframe("past") },
          { key: "virtual", label: "Virtual", value: timeframeCounts.virtual, icon: Video,
            color: "#00D4FF", background: "rgba(0,212,255,.10)", border: "rgba(0,212,255,.24)",
            selected: typeFilter === "virtual",
            onSelect: () => setTypeFilter(typeFilter === "virtual" ? "all" : "virtual") },
          { key: "in-person", label: "In-person", value: timeframeCounts.inPerson, icon: MapPin,
            color: "#C4B5FD", background: "rgba(139,92,246,.12)", border: "rgba(139,92,246,.28)",
            selected: typeFilter === "in-person",
            onSelect: () => setTypeFilter(typeFilter === "in-person" ? "all" : "in-person") },
        ]}
      />

      {/*
        Shown only while code-defined events remain, and it disappears once they are
        all migrated -- a permanent button for a one-time action invites a second,
        pointless run. The action itself is idempotent regardless.
      */}
      {unmigratedCount > 0 && (
        <div role="note" style={{ display: "flex", gap: ".9rem", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", marginBottom: "1.5rem", padding: ".9rem 1rem", borderRadius: ".8rem", background: "rgba(245,166,35,.07)", border: "1px solid rgba(245,166,35,.26)" }}>
          <div style={{ maxWidth: 640 }}>
            <p style={{ margin: "0 0 .25rem", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}>
              {unmigratedCount} event{unmigratedCount === 1 ? " is" : "s are"} defined in code, not in the database
            </p>
            <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
              They cannot be edited here, never get a Zoom meeting provisioned, and send
              no calendar invite when someone registers. Migrating copies them into the
              database keeping every slug exactly as it is, so existing registrations and
              speaker assignments stay attached. Nothing is overwritten or removed, and
              running it twice does nothing the second time.
            </p>
          </div>
          <button
            type="button"
            disabled={migrateCanonicalEvents.isPending}
            onClick={() => migrateCanonicalEvents.mutate()}
            style={{ minHeight: 40, display: "inline-flex", alignItems: "center", gap: ".4rem", padding: "0 .9rem", border: "1px solid rgba(245,166,35,.45)", borderRadius: ".55rem", background: "rgba(245,166,35,.14)", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: migrateCanonicalEvents.isPending ? "wait" : "pointer", whiteSpace: "nowrap" }}
          >
            {migrateCanonicalEvents.isPending ? "Migrating…" : "Migrate into database"}
          </button>
        </div>
      )}

      <div
        role="region"
        aria-label="Event artwork generation"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem", padding: ".9rem 1rem", borderRadius: ".8rem", background: "rgba(0,212,255,.05)", border: "1px solid rgba(0,212,255,.18)" }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: "0 0 .3rem", color: "#E0F2FE", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}>
            Branded event artwork
          </p>
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
            {graphicCounts.ready} ready · {graphicCounts.processing} processing · {graphicCounts.failed} failed · {graphicCounts.not_requested} not generated
          </p>
          <p style={{ margin: ".18rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
            Featured hero: {heroGraphicCounts.ready} ready · {heroGraphicCounts.processing} processing · {heroGraphicCounts.failed} failed · {heroGraphicCounts.not_requested} not generated
          </p>
        </div>
        <button
          type="button"
          disabled={backfillGraphics.isPending}
          onClick={() => backfillGraphics.mutate({})}
          style={{ minHeight: 40, display: "inline-flex", alignItems: "center", gap: ".4rem", padding: "0 .9rem", border: "1px solid rgba(0,212,255,.35)", borderRadius: ".55rem", background: "rgba(0,212,255,.1)", color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: backfillGraphics.isPending ? "wait" : "pointer", whiteSpace: "nowrap" }}
        >
          {backfillGraphics.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          {backfillGraphics.isPending ? "Queueing…" : "Generate missing artwork"}
        </button>
      </div>

      <CreateEventDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      {/*
        Filter bar, left to right: which set of events, then narrowing within it.

        The timeframe toggle leads because it chooses the list — searching inside
        Upcoming and searching inside Past are different questions, and the control
        that decides which one you are asking belongs before the box you type into.
        The speaker filter sits hard right, pushed there by the spacer, because it is
        the one an administrator sets occasionally rather than per look.
      */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        {/*
          Upcoming and past used to be a pill group here. They are tiles above the
          table now — one control per idea, rather than a count in one place and
          the switch that acts on it in another.
        */}
        <div style={{ position: "relative", flex: "0 1 320px", minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)", color: "#475569" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search events…"
            style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.625rem", padding: "0.625rem 0.875rem 0.625rem 2.25rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", outline: "none" }}
          />
          {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#475569", cursor: "pointer", padding: 0 }}><X size={13} /></button>}
        </div>

        {/* Takes the slack, so the speaker filter is pinned to the right edge. */}
        <div style={{ flex: "1 1 0", minWidth: 0 }} aria-hidden="true" />

        {/*
          A type-ahead rather than a native select: this list is every speaker who
          has an event, so it grows with the roster and by a few dozen names the
          popup is a scroll with no search. Same control as the speaker and event
          pickers in the Speakers drawer, so the two read as one pattern.

          The "Filter by Speaker(s)" sentinel is an option rather than a cleared
          value, because the filter always has a state and the sentinel is what
          names it — and naming it as the action makes the control's purpose
          legible before it is opened.
        */}
        <div style={{ flex: "0 1 260px", minWidth: 200 }}>
          <SearchableSelect
            ariaLabel="Filter events by speaker"
            placeholder="Filter by Speaker(s)"
            searchPlaceholder="Type a speaker's name…"
            emptyLabel="No speaker matches that name"
            value={speakerFilter}
            onChange={setSpeakerFilter}
            options={[
              { value: "all", label: "Filter by Speaker(s)" },
              ...speakerOptions.map(speaker => ({ value: speaker, label: speaker })),
            ]}
          />
        </div>
        {speakerFilter !== "all" && (
          <button
            type="button"
            onClick={() => setSpeakerFilter("all")}
            style={{ minHeight: 38, padding: "0 0.75rem", borderRadius: "0.625rem", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}
          >
            Clear speaker
          </button>
        )}
      </div>

      {/* Table */}
      <div className="events-management-table" style={{ background: "var(--chi-table-surface)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "0.875rem", overflow: "hidden" }}>
        {/* Table header */}
        {/*
          Keyed on `key` rather than on the label, because the portrait column is
          deliberately unlabelled — a face needs no heading — and an empty string
          is not a usable React key.
        */}
        {/*
          Header styling matched to the `th` of every other CRM table: those are
          #7E8CA0 at weight 750, and this one was #475569 at 700, so the events
          table read as a different component from the members, speakers and
          payments tables beside it in the same sidebar.
        */}
        <div className="events-management-table__header" style={{ display: "grid", gridTemplateColumns: EVENT_ROW_COLUMNS, columnGap: EVENT_ROW_COLUMN_GAP, padding: "0.75rem 1.25rem", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          {[
            { key: "portrait", label: "" },
            { key: "event", label: "Event" },
            { key: "featuredImage", label: "Featured Image" },
            { key: "speakers", label: "Speaker(s)" },
            { key: "date", label: "Date" },
            { key: "type", label: "Type" },
            { key: "registered", label: "Registered" },
            { key: "actions", label: "Actions" },
          ].map(column => (
            <span key={column.key} className="crm-grid-table__heading"><CrmColumnHeading label={column.label} /></span>
          ))}
        </div>

        {filtered.map((evt, i) => {
          const fill = Math.round((evt.registered / evt.seats) * 100);
          const isPast = isPastCalendarEvent(evt, eventClock);
          const eventSpeakers = speakersForEvent(evt, speakersByEventSlug);
          return (
            <EventRecordContextMenu
              key={evt.id}
              event={evt}
              onOpen={() => onPreviewEvent(evt)}
              onViewAttendees={() => onViewRoster(evt)}
              onEdit={() => onPreviewEvent(evt, true)}
              onLinkZoom={() => setZoomLinkEvent(evt)}
              onArchive={() => setPendingAction({ kind: "archive", event: evt })}
              onDelete={() => setPendingAction({ kind: "delete", event: evt })}
            >
              {/*
                The row opens the event, not its roster.

                Clicking an event and landing on a list of attendees answered a
                narrower question than the one the click asks: the row is the event,
                so it opens the event. The roster is one of the things the drawer
                offers, alongside editing and the Zoom link, and it is still reachable
                directly from the row's actions menu and by right-clicking it — so
                nothing that used to be one click away is now more than two.
              */}
              <div
              className="events-management-row"
              onClick={() => onPreviewEvent(evt)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onPreviewEvent(evt);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Open event details for ${evt.title}`}
              style={{
                display: "grid", gridTemplateColumns: EVENT_ROW_COLUMNS, columnGap: EVENT_ROW_COLUMN_GAP,
                /*
                  Extra room on the first row only. It sat directly against the
                  divider under the column labels, so the first event's title read
                  as part of the header rather than as the first record — the
                  divider had nothing on one side of it. Rows below keep their own
                  rhythm.
                */
                padding: i === 0 ? "1.6rem 1.25rem 1rem" : "1rem 1.25rem",
                alignItems: "center",
                borderBottom: i < filtered.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                cursor: "pointer", transition: "background 0.15s ease",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              {/* Speaker portrait — its own column, deliberately without a heading. */}
              <div className="events-management-row__portrait">
                <EventSpeakerPortraits speakers={eventSpeakers} />
              </div>
              {/*
                Title. No colour dot: the portrait column ahead of it now carries
                the row's visual identity, and a second marker on the same row was
                decoration competing with a face.
              */}
              {/*
                One line each, truncated. A long title used to wrap and push the
                row taller than its neighbours, so a list of events had a ragged
                rhythm set by whichever title happened to be longest. The full
                text stays available on hover and to assistive technology.
              */}
              {/*
                A gutter on the right as well: a title long enough to ellipsize
                otherwise runs its last character straight into the speaker's
                name in the next column.
              */}
              <div className="events-management-row__title" style={{ minWidth: 0 }}>
                <p className="events-management-row__truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: "white", marginBottom: "0.1rem" }}>{evt.title}</p>
                {evt.subtitle && <p className="events-management-row__truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#475569" }}>{evt.subtitle}</p>}
                 <EventGraphicStatus
                   graphic={evt.graphic}
                   disabled={retryGraphic.isPending}
                   onRetry={administratorEventArtwork(evt.heroImage) ? undefined : () => {
                     retryGraphic.mutate({ eventSlug: evt.slug });
                   }}
                 />
                 {!administratorEventArtwork(evt.heroImage) && (
                   <EventGraphicStatus
                     graphic={evt.graphic?.heroGraphic}
                     label="Featured hero"
                     disabled={retryGraphic.isPending}
                     onRetry={() => retryHeroGraphic.mutate({ eventSlug: evt.slug })}
                   />
                 )}
                {/*
                  The whole name and description, for the rows where the column
                  clipped them. `role="tooltip"` and `aria-hidden` off, so a
                  screen reader reads the full text rather than the ellipsis.
                */}
                <span className="events-management-row__tip" role="tooltip">
                  <strong>{evt.title}</strong>
                  {evt.subtitle && <span>{evt.subtitle}</span>}
                  {evt.description && <span>{evt.description}</span>}
                </span>
              </div>
              {/* Featured image — a consistent 16:9 visual slot for every event. */}
              <EventFeaturedImage event={evt} />
              {/* Speaker(s) */}
              <div className="events-management-row__speakers" style={{ minWidth: 0 }}>
                <EventSpeakerNames speakers={eventSpeakers} />
              </div>
              {/* Date */}
              <span className="events-management-row__date" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: isPast ? "#475569" : "#94A3B8", whiteSpace: "nowrap" }}>{evt.displayDate}</span>
              {/* Type */}
              <span className="events-management-row__type" style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: evt.type === "virtual" ? "#00D4FF" : "#F5A623", background: evt.type === "virtual" ? "rgba(0,212,255,0.08)" : "rgba(245,166,35,0.08)", border: `1px solid ${evt.type === "virtual" ? "rgba(0,212,255,0.2)" : "rgba(245,166,35,0.2)"}`, borderRadius: "2rem", padding: "0.2rem 0.6rem", justifyContent: "center" }}>
                {evt.type === "virtual" ? "Virtual" : "In-Person"}
              </span>
              {/* Registered */}
              <span className="events-management-row__registered" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "white" }}>
                <span style={{ fontWeight: 700 }}>{evt.registered}</span>
                <span style={{ color: "#475569" }}> / {evt.seats}</span>
              </span>

                {/* The roster opens from the row itself. All record operations are
                    intentionally consolidated into the accessible Actions menu so
                    the table retains a calm, predictable final column. */}
                <div className="events-management-row__actions" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.375rem" }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} onContextMenu={e => e.stopPropagation()}>
                  <EventRecordActionDropdown
                    event={evt}
                    onOpen={() => onPreviewEvent(evt)}
                    onViewAttendees={() => onViewRoster(evt)}
                    onEdit={() => onPreviewEvent(evt, true)}
                    onLinkZoom={() => setZoomLinkEvent(evt)}
                    onArchive={() => setPendingAction({ kind: "archive", event: evt })}
                    onDelete={() => setPendingAction({ kind: "delete", event: evt })}
                  />
                </div>
              </div>
            </EventRecordContextMenu>
          );
        })}

        {filtered.length === 0 && (
          <div style={{ padding: "3rem", textAlign: "center", color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            No events match your search.
          </div>
        )}
      </div>

      {zoomLinkEvent && (
        <LinkZoomMeetingDialog
          eventSlug={zoomLinkEvent.slug}
          eventTitle={zoomLinkEvent.title}
          onClose={() => setZoomLinkEvent(null)}
        />
      )}

      <AlertDialog open={pendingAction !== null} onOpenChange={open => { if (!open) setPendingAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingAction?.kind === "archive" ? "Archive this event?" : "Delete this empty event?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.kind === "archive"
                ? `“${pendingAction.event.title}” will be removed from the active events list. Existing registrations and operational history remain intact.`
                : `“${pendingAction?.event.title}” will be permanently removed only if it has no attendee or operational history. Otherwise, archive it instead.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveEvent.isPending || deleteEvent.isPending}
              className={pendingAction?.kind === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : "bg-amber-500 text-black hover:bg-amber-400"}
              onClick={() => {
                if (!pendingAction) return;
                if (pendingAction.kind === "archive") archiveEvent.mutate({ slug: pendingAction.event.slug });
                else deleteEvent.mutate({ slug: pendingAction.event.slug });
              }}
            >
              {archiveEvent.isPending || deleteEvent.isPending ? "Working…" : pendingAction?.kind === "archive" ? "Archive event" : "Delete event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Attendee Roster View ───────────────────────────────────────────────────────
/**
 * The roster, in a drawer.
 *
 * "View attendees" used to replace the whole events workspace with a full page,
 * so glancing at who had signed up meant leaving the list and navigating back.
 * This answers the question in place. The full workspace is still there for the
 * things a list of names cannot do — export, messaging, search — and is one
 * click away from the footer.
 */
function AttendeeRosterDrawer({ event, onOpenFullRoster, onClose }: {
  event: AdminEvent;
  onOpenFullRoster: () => void;
  onClose: () => void;
}) {
  const rosterQuery = trpc.events.attendeeRoster.useQuery(
    { eventSlug: event.slug },
    { enabled: Boolean(event.slug), retry: false },
  );
  const roster = rosterQuery.data ?? [];

  return (
    <Sheet open onOpenChange={next => { if (!next) onClose(); }}>
      <SheetContent side="right" className="speaker-create-drawer">
        <div className="crm-drawer">
          <SheetHeader className="crm-drawer__header">
            <SheetTitle className="crm-drawer__title">Attendees</SheetTitle>
            <SheetDescription className="crm-drawer__subtitle">
              {event.title}
            </SheetDescription>
          </SheetHeader>

          <div className="crm-drawer__body">
            {rosterQuery.isLoading ? (
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Loading the roster…</p>
            ) : roster.length === 0 ? (
              /* Said plainly rather than shown as an empty list, which reads as a
                 roster that failed to load. */
              <p style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
                Nobody has registered for this event yet.
              </p>
            ) : (
              <>
                <p style={{ margin: "0 0 .6rem", color: "#7E8CA0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                  {roster.length} registered
                </p>
                <ul style={{ display: "grid", gap: ".35rem", margin: 0, padding: 0, listStyle: "none" }}>
                  {roster.map((person, index) => (
                    <li
                      key={`${person.email}-${index}`}
                      style={{ display: "grid", gap: ".1rem", padding: ".6rem .7rem", border: "1px solid rgba(255,255,255,.07)", borderRadius: ".6rem", background: "rgba(0,0,0,.16)" }}
                    >
                      <strong style={{ color: "#F4F7FA", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{person.fullName}</strong>
                      <span style={{ overflow: "hidden", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", textOverflow: "ellipsis" }}>
                        {[person.jobTitle, person.company].filter(Boolean).join(" · ") || person.email}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="crm-drawer__footer">
            <button type="button" className="crm-drawer__secondary" onClick={onClose}>Close</button>
            <button type="button" className="crm-drawer__primary" onClick={onOpenFullRoster}>
              Open full roster
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AttendeesView({ eventId, onBack }: { eventId: number; onBack: () => void }) {
  const [search, setSearch] = useState("");
  const [msgTarget, setMsgTarget] = useState<EventAttendeeRow | null>(null);
  const [msgText, setMsgText] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isAddingAttendees, setIsAddingAttendees] = useState(false);
  const utils = trpc.useUtils();

  /*
   * Resolved across both event sources, not just the code-defined array.
   *
   * A database-managed event is exposed with `id: 100_000 + row.id` to keep it from
   * colliding with a code-defined id, so looking only in EVENTS returned undefined
   * for every managed event — and the `if (!evt) return null` below then rendered a
   * blank page instead of the roster. That already affected any event created
   * through the Create Event dialog, and would have hit all eleven code-defined
   * events the moment they were migrated into managedEvents.
   */
  const managedCatalogQuery = trpc.events.managedEvents.useQuery(undefined, {
    retry: false,
    refetchInterval: paidEventCatalogRefetchInterval,
    refetchOnWindowFocus: true,
  });
  const evt = useMemo(() => {
    const managed = (managedCatalogQuery.data ?? []) as CHIEvent[];
    // Managed wins on a slug collision, matching every other merge in this file.
    const managedSlugs = new Set(managed.map(event => event.slug));
    return [...managed, ...EVENTS.filter(event => !managedSlugs.has(event.slug))]
      .find(event => event.id === eventId);
  }, [managedCatalogQuery.data, eventId]);

  // The roster shows durable registration records only. It previously rendered a
  // generated sample roster, which presented invented people as real attendees.
  const rosterQuery = trpc.events.attendeeRoster.useQuery(
    { eventSlug: evt?.slug ?? "" },
    { enabled: Boolean(evt?.slug), retry: false },
  );
  /* Main's roster has review fields but not the registration id. The queue
   * already returns `id` for pending_review rows, which is the only place
   * Decline is offered. */
  const pendingQueueQuery = trpc.events.listPending.useQuery(
    { eventId: evt?.slug ?? "", status: "pending", limit: 100 },
    { enabled: Boolean(evt?.slug), retry: false },
  );
  const pendingIdByEmail = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of pendingQueueQuery.data?.items ?? []) {
      map.set(item.email.trim().toLowerCase(), item.id);
    }
    return map;
  }, [pendingQueueQuery.data]);

  const allAttendees = useMemo<EventAttendeeRow[]>(
    () => mapEventRosterRows((rosterQuery.data ?? []) as unknown as EventRosterApiRow[], pendingIdByEmail),
    [pendingIdByEmail, rosterQuery.data],
  );

  const filtered = useMemo(
    () => filterEventRosterRows(allAttendees, search),
    [allAttendees, search],
  );

  if (!evt) return null;

  const exportSelectedEventCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const rows = await utils.events.attendeeExport.fetch({ eventSlug: evt.slug });
      if (rows.length === 0) {
        chiNotify.info("No durable attendee records", {
          description: "This event has no saved registration or attendance records to export.",
        });
        return;
      }

      const csv = buildEventAttendeeCsv(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const safeSlug = evt.slug.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "event";
      anchor.href = url;
      anchor.download = `chi-alliance-${safeSlug}-attendees.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      chiNotify.success(`${rows.length} ${rows.length === 1 ? "attendee" : "attendees"} exported`, {
        description: "The CSV includes only this event’s registration and attendance fields.",
      });
    } catch (error) {
      chiNotify.error("Export unavailable", {
        description: error instanceof Error ? error.message : "The attendee CSV could not be generated.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const confirmed = allAttendees.filter(a => a.status === "confirmed").length;
  const waitlist  = allAttendees.filter(a => a.status === "waitlist").length;
  const incomplete = allAttendees.filter(a =>
    ["checkout_pending", "checkout_expired", "checkout_cancelled", "payment_failed", "review", "ticket_review"].includes(a.status),
  ).length;
  const rosterUnavailable = rosterQuery.isError;
  const rosterLoading = rosterQuery.isLoading;

  return (
    <div style={{ padding: "2rem", flex: 1, overflowY: "auto" }}>
      {/* Back */}
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer", marginBottom: "1.25rem", padding: 0 }}
        onMouseEnter={e => { e.currentTarget.style.color = "white"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#64748B"; }}
      >
        <ChevronLeft size={14} /> Back to Events
      </button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.35rem" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: evt.color }} />
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.6rem", color: "white", letterSpacing: "0.04em" }}>{evt.title}</h1>
          </div>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B" }}>
            {evt.displayDate} · {evt.time} · {evt.location}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.625rem" }}>
          <button
            type="button"
            onClick={exportSelectedEventCsv}
            disabled={isExporting}
            aria-label={`Export ${evt.title} attendees as CSV`}
            title="Export saved registration and attendance fields as CSV"
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.6rem 1rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: isExporting ? "wait" : "pointer", opacity: isExporting ? 0.7 : 1 }}
          >
            {isExporting ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} {isExporting ? "Preparing…" : "Export CSV"}
          </button>
          <button
            type="button"
            onClick={() => setIsAddingAttendees(true)}
            aria-label={`Add attendees to ${evt.title}`}
            title="Add members to this event, several at a time"
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.6rem 1rem", background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.3)", borderRadius: "0.5rem", color: "#00D4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
          >
            <UserPlus size={13} /> Add Attendees
          </button>
          <button
            onClick={() => chiNotify.info("Message All — coming in next release")}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.6rem 1rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", border: "none", borderRadius: "0.5rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(233,30,140,0.28)" }}
          >
            <Mail size={13} /> Message All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <StatCard label="Total Registered" value={allAttendees.length} sub={`of ${evt.seats} seats`} color="#E91E8C" />
        <StatCard label="Confirmed" value={confirmed} sub="attendees" color="#22C55E" />
        <StatCard label="Waitlist" value={waitlist} sub="pending" color="#F5A623" />
        {incomplete > 0 && <StatCard label="Incomplete" value={incomplete} sub="checkout / review" color="#60A5FA" />}
        <StatCard label="Seats Remaining" value={getEventSeatsRemaining(evt, confirmed)} sub="available" color="#00D4FF" />
      </div>

      {/* Search + filter */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 360 }}>
          <Search size={14} style={{ position: "absolute", left: "0.875rem", top: "50%", transform: "translateY(-50%)", color: "#475569" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search attendees…"
            style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.625rem", padding: "0.625rem 0.875rem 0.625rem 2.25rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", outline: "none" }}
          />
          {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#475569", cursor: "pointer", padding: 0 }}><X size={13} /></button>}
        </div>
        <button
          onClick={() => chiNotify.info("Filter — coming in next release")}
          style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.625rem 1rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "0.625rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}
        >
          <Filter size={13} /> Filter
        </button>
      </div>

      {/* Attendee Table */}
      <div className="event-attendee-table" style={{ background: "var(--chi-table-surface)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "0.875rem", overflow: "hidden" }}>
        {/* Header */}
        {/* Keyed on `key`: the portrait column is deliberately unlabelled, and an empty string is not a usable React key. */}
        <div className="event-attendee-table__header" style={{ display: "grid", gridTemplateColumns: ATTENDEE_ROW_COLUMNS, padding: "0.75rem 1.25rem", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          {[
            { key: "portrait", label: "" },
            { key: "person", label: "Name / Title" },
            { key: "email", label: "Email" },
            { key: "company", label: "Company" },
            { key: "registered", label: "Registered" },
            { key: "status", label: "Status" },
            { key: "actions", label: "Actions" },
          ].map(column => (
            <span key={column.key} className="crm-grid-table__heading"><CrmColumnHeading label={column.label} /></span>
          ))}
        </div>

        {filtered.map((att, i) => (
          <div
            key={att.id}
            className="event-attendee-row"
            style={{
              display: "grid", gridTemplateColumns: ATTENDEE_ROW_COLUMNS,
              // Same breathing room under this table's header as the events one.
              padding: i === 0 ? "1.45rem 1.25rem 0.875rem" : "0.875rem 1.25rem",
              alignItems: "center",
              borderBottom: i < filtered.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
              borderLeft: att.rowKind === "guest" ? "2px solid rgba(0,212,255,.24)" : "2px solid transparent",
              background: att.rowKind === "guest" ? "rgba(0,212,255,.018)" : "transparent",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.02)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            {/* Photo — its own column, deliberately without a heading. */}
            <div className="event-attendee-row__portrait">
              <CrmPortraitAvatar identity={{ key: att.id, name: att.name, photoUrl: att.photoUrl, memberPhotoUrl: att.memberPhotoUrl }} size={CRM_ROW_PORTRAIT_SIZE} />
            </div>
            {/* Name */}
            <div className="event-attendee-row__person" style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".4rem", minWidth: 0 }}>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: "white", marginBottom: "0.1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name}</p>
                {att.rowKind === "purchaser" && <span style={{ flexShrink: 0, color: "#00D4FF", border: "1px solid rgba(0,212,255,.22)", borderRadius: "2rem", padding: ".1rem .4rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.3 }}>Ticket purchaser</span>}
              </div>
              {att.rowKind === "guest" && (
                <div style={{ display: "flex", alignItems: "center", gap: ".4rem", margin: ".15rem 0 .2rem", paddingLeft: ".55rem", borderLeft: "2px solid rgba(0,212,255,.3)" }}>
                  <span style={{ color: "#8E9AAF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>Guest of {att.purchaserName || "ticket purchaser"}</span>
                  <span style={{ color: "#00D4FF", border: "1px solid rgba(0,212,255,.22)", borderRadius: "2rem", padding: ".05rem .4rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>Guest</span>
                </div>
              )}
              {att.rowKind === "guest" && <span style={{ display: "block", paddingLeft: ".55rem", color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>{att.title}</span>}
              {att.rowKind !== "guest" && <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.title}</p>}
            </div>
            {/* Email */}
            <a className="event-attendee-row__email" href={`mailto:${att.email}`} style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.3rem", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#00D4FF"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "#64748B"; }}
            >
              <Mail size={11} /> {att.email}
            </a>
            {/* Company */}
            <span className="event-attendee-row__company" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#94A3B8" }}>{att.company}</span>
            {/* Registered */}
            <span className="event-attendee-row__registered" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#475569" }}>{att.registeredAt}</span>
            {/* Status */}
            <span className="event-attendee-row__status"><StatusBadge status={att.status} /></span>
            {/* Actions */}
            <div className="event-attendee-row__actions" style={{ display: "flex", gap: "0.3rem" }}>
              {canShowMemberProfileAction(att) && <button
                title="View profile"
                onClick={() => chiNotify.info(`View profile for ${att.name} — coming in next release`)}
                style={{ width: 28, height: 28, borderRadius: "0.375rem", background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.18)", color: "#A855F7", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(168,85,247,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(168,85,247,0.08)"; }}
              >
                <Eye size={12} />
              </button>}
              <button
                title="Send message"
                onClick={() => { setMsgTarget(att); setMsgText(""); }}
                style={{ width: 28, height: 28, borderRadius: "0.375rem", background: "rgba(233,30,140,0.08)", border: "1px solid rgba(233,30,140,0.18)", color: "#E91E8C", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(233,30,140,0.18)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(233,30,140,0.08)"; }}
              >
                <MessageSquare size={12} />
              </button>
              {canManuallyReviewEventRosterRow(att) && att.registrationId != null && evt.slug && (
                <DeclineRegistrationButton
                  registrationId={att.registrationId}
                  expectedVersion={att.reviewVersion}
                  attendeeName={att.name}
                  eventSlug={evt.slug}
                />
              )}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ padding: "3rem", textAlign: "center", color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
            {rosterLoading
              ? "Loading registrations…"
              : rosterUnavailable
                ? "This event’s registrations could not be loaded. Retry, or check that you are signed in as an administrator."
                : allAttendees.length === 0
                  ? "No one has registered for this event yet. Only saved registration records appear here."
                  : "No attendees match your search."}
          </div>
        )}
      </div>

      {isAddingAttendees && (
        <AddEventAttendeesDialog
          eventSlug={evt.slug}
          eventTitle={evt.title}
          onClose={() => setIsAddingAttendees(false)}
        />
      )}

      {/* Message Composer Modal */}
      {msgTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={() => setMsgTarget(null)}
        >
          <div
            style={{ background: "#0D1220", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "1rem", padding: "1.75rem", width: "100%", maxWidth: 480, boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
              <div>
                <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.2rem", color: "white", letterSpacing: "0.04em" }}>Message Attendee</h3>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B" }}>To: {msgTarget.name} · {msgTarget.email}</p>
              </div>
              <button onClick={() => setMsgTarget(null)} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <textarea
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
              placeholder="Type your message here…"
              rows={5}
              style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.625rem", padding: "0.875rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", resize: "vertical", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", justifyContent: "flex-end" }}>
              <button onClick={() => setMsgTarget(null)} style={{ padding: "0.6rem 1.25rem", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}>Cancel</button>
              <button
                onClick={() => { chiNotify.success(`Message to ${msgTarget.name} queued (POC — not sent)`); setMsgTarget(null); }}
                style={{ padding: "0.6rem 1.25rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", border: "none", borderRadius: "0.5rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Placeholder views ──────────────────────────────────────────────────────────
function PlaceholderView({ title, description }: { title: string; description: string }) {
  return (
    <div style={{ padding: "2rem", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ width: 56, height: 56, borderRadius: "1rem", background: "rgba(233,30,140,0.1)", border: "1px solid rgba(233,30,140,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem" }}>
          <ArrowUpRight size={24} style={{ color: "#E91E8C" }} />
        </div>
        <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.5rem", color: "white", letterSpacing: "0.04em", marginBottom: "0.5rem" }}>{title}</h2>
        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B", lineHeight: 1.7 }}>{description}</p>
        <div style={{ marginTop: "1rem", display: "inline-flex", alignItems: "center", gap: "0.4rem", background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.2)", borderRadius: "2rem", padding: "0.35rem 0.875rem" }}>
          <Clock size={12} style={{ color: "#F5A623" }} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, color: "#F5A623" }}>Coming in next release</span>
        </div>
      </div>
    </div>
  );
}

// ── Operations dashboard ───────────────────────────────────────────────────────
function DashboardView() {
  return <OperationsHealthView />;
}

// ── Main AdminCRM ──────────────────────────────────────────────────────────────
export default function AdminCRM() {
  const { user } = useAuth();
  const [location, navigate] = useLocation();
  const speakerMemberOpenId = useMemo(
    () => new URLSearchParams(location.split("?")[1] ?? "").get("speakerMember"),
    [location],
  );
  const serverAuth = trpc.auth.me.useQuery(undefined, { retry: false });
  const ownerAccess = trpc.auth.ownerAccess.useQuery(undefined, { retry: false });
  const [activeView, setActiveView] = useState<string>(() => {
    if (typeof window === "undefined") return "dashboard";
    const params = new URLSearchParams(window.location.search);
    const requestedView = params.get("view");
    if (requestedView === "attendees") return "members";
    if (["dashboard", "events", "approvals", "speakers", "members", "groups", "payments", "messages", "ticket-flags", "website-content", "community-hero", "speakers-featured", "maintenance", "settings"].includes(requestedView ?? "")) {
      return requestedView!;
    }
    return params.has("googleWorkspace") ? "settings" : "dashboard";
  });
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  /** The event whose roster is open in the drawer, if any. */
  const [rosterEvent, setRosterEvent] = useState<AdminEvent | null>(null);
  const [previewEvent, setPreviewEvent] = useState<{ event: AdminEvent; startInEditMode: boolean } | null>(null);

  const hasServerAdmin = serverAuth.data?.role === "admin";
  const hasServerOwner = ownerAccess.data?.isProjectOwner === true;

  // The CRM is gated by the persisted server role. A browser-only profile never grants access.
  if (serverAuth.isLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#080C18", display: "grid", placeItems: "center", color: "#94A3B8" }}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!hasServerAdmin) {
    return (
      <div style={{ minHeight: "100vh", background: "#080C18", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem" }}>
        <Shield size={40} style={{ color: "#E91E8C" }} />
        <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.5rem", color: "white" }}>Admin Access Required</h2>
        <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", color: "#64748B" }}>Please sign in with an admin account to continue.</p>
        <button onClick={() => navigate("/login?returnTo=%2Fadmin")} style={{ padding: "0.65rem 1.5rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", border: "none", borderRadius: "0.625rem", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}>
          Sign in securely
        </button>
      </div>
    );
  }

  function handleNav(view: string) {
    setActiveView(view);
    setSelectedEventId(null);
    navigate(view === "dashboard" ? "/admin" : `/admin?view=${encodeURIComponent(view)}`);
  }

  /**
   * Jump from a speaker's assigned event to that event in the Events workspace.
   *
   * The speaker record names the talk but could not reach it, so checking an
   * assignment meant switching tabs and searching for the title by hand.
   */
  function openEventFromSpeaker(eventSlug: string) {
    setActiveView("events");
    setSelectedEventId(null);
    navigate(`/admin?view=events&event=${encodeURIComponent(eventSlug)}`);
  }

  /**
   * Opens Events narrowed to one speaker.
   *
   * Carried in the URL rather than lifted into shared state, matching how a single
   * event is opened from a speaker just above. The filter then survives a reload and
   * the link can be handed to someone else — and Events keeps owning its own filter
   * state instead of having it driven from two places.
   */
  function viewSpeakerEvents(speakerName: string) {
    setActiveView("events");
    setSelectedEventId(null);
    navigate(`/admin?view=events&speaker=${encodeURIComponent(speakerName)}`);
  }

  function beginSpeakerProfileFromMember(userOpenId: string) {
    setActiveView("speakers");
    setSelectedEventId(null);
    navigate(`/admin?view=speakers&speakerMember=${encodeURIComponent(userOpenId)}`);
  }

  /** Edit on the Website Content screen: open the editor that already owns the content. */
  function openContentEditor(view: WebsiteContentEditorView) {
    setActiveView(view);
    setSelectedEventId(null);
    navigate(`/admin?view=${encodeURIComponent(view)}`);
  }

  function renderContent() {
    if (activeView === "events" && selectedEventId !== null) {
      return <AttendeesView eventId={selectedEventId} onBack={() => setSelectedEventId(null)} />;
    }
    switch (activeView) {
      case "dashboard": return <DashboardView />;
      case "events":    return <EventsView onSelectEvent={(id) => { setSelectedEventId(id); }} onPreviewEvent={(event, startInEditMode = false) => setPreviewEvent({ event, startInEditMode })} onViewRoster={setRosterEvent} onComposeEventUpdate={() => setActiveView("messages")} />;
      /*
        Reachable at ?view=speakers-featured but no longer in the sidebar. Featuring
        a speaker is an action on the speaker — right-click › Set as Featured
        Speaker — so the nav entry was a second route to the same outcome. The page
        remains for the two things the menu cannot express: card order and the
        headline written for the card.
      */
      case "speakers-featured": return <FeaturedSpeakersCRMView />;
      case "approvals": return <RegistrationApprovalQueueView />;
      case "speakers":  return <SpeakerCRMView memberOpenId={speakerMemberOpenId} onOpenEvent={openEventFromSpeaker} onViewSpeakerEvents={viewSpeakerEvents} />;
      case "members":   return <MembershipManagementView onStartSpeakerProfile={beginSpeakerProfileFromMember} />;
      case "groups":    return <MemberGroupManagementView />;
      case "payments":  return <PaymentRecordsView />;
      case "messages":  return <MessageCampaignView />;
      case "ticket-flags": return <TicketFlagsView />;
      case "website-content": return <WebsiteContentView onOpenEditor={openContentEditor} />;
      case "community-hero": return <CommunityHeroControlsView />;
      case "maintenance": return <MaintenanceView />;
      case "settings":
        if (serverAuth.isLoading || ownerAccess.isLoading) {
          return (
            <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#94A3B8" }}>
              <Loader2 className="animate-spin" aria-label="Checking secure owner session" />
            </div>
          );
        }
        if (!hasServerOwner) {
          return (
            <div style={{ flex: 1, display: "grid", placeItems: "center", padding: "2rem" }}>
              <div role="region" aria-labelledby="workspace-owner-sign-in" style={{ width: "min(100%, 620px)", padding: "clamp(1.5rem,4vw,2.5rem)", borderRadius: "1.25rem", background: "linear-gradient(145deg,rgba(233,30,140,.1),rgba(13,18,32,.98) 42%,rgba(0,212,255,.06))", border: "1px solid rgba(255,255,255,.1)", textAlign: "center", boxShadow: "0 24px 70px rgba(0,0,0,.28)" }}>
                <Shield size={34} style={{ margin: "0 auto 1rem", color: "#E91E8C" }} />
                <h2 id="workspace-owner-sign-in" style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.65rem,3vw,2.25rem)" }}>Secure owner sign-in required</h2>
                <p style={{ maxWidth: 500, margin: "0.8rem auto 1.4rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", lineHeight: 1.65 }}>Google Workspace authorization and delivery tests require the project owner’s verified server session. The existing browser-only admin session cannot access mailbox credentials or tokens.</p>
                {(serverAuth.error || ownerAccess.error) && (
                  <p role="alert" style={{ margin: "0 0 1rem", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{serverAuth.error?.message ?? ownerAccess.error?.message}</p>
                )}
                <button type="button" onClick={() => navigate("/login?returnTo=%2Fadmin%3Fview%3Dsettings")} style={{ minHeight: 46, padding: "0.75rem 1.25rem", border: "1px solid rgba(233,30,140,.5)", borderRadius: "0.7rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}>Continue with secure owner sign-in</button>
              </div>
            </div>
          );
        }
        return (
          /*
            Both panels used to be open on the page at once. They are now behind
            their connector tiles in ConnectorSettings, which owns the layout.
          */
          <div style={{ flex: 1, overflowY: "auto" }}>
            <ConnectorSettings />
          </div>
        );
      default:          return <EventsView onSelectEvent={(id) => setSelectedEventId(id)} onPreviewEvent={(event, startInEditMode = false) => setPreviewEvent({ event, startInEditMode })} onViewRoster={setRosterEvent} onComposeEventUpdate={() => setActiveView("messages")} />;
    }
  }

  return (
    <div className="admin-crm-shell" style={{ display: "flex", minHeight: "100vh", background: "#080C18" }}>
      <Sidebar active={activeView} onNav={handleNav} />
      <main className="admin-crm-main" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <header className="admin-crm-topbar">
          <p>Admin workspace</p>
          <div>
            <NotificationBell admin />
          </div>
        </header>
        <div className="admin-crm-content" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {renderContent()}
        </div>
      </main>
      {rosterEvent && (
        <AttendeeRosterDrawer
          event={rosterEvent}
          onClose={() => setRosterEvent(null)}
          onOpenFullRoster={() => { setSelectedEventId(rosterEvent.id); setRosterEvent(null); }}
        />
      )}
      {previewEvent && (
        <EventDetailModal
          event={previewEvent.event}
          startInEditMode={previewEvent.startInEditMode}
          onClose={() => setPreviewEvent(null)}
          onViewAttendees={(id) => { setSelectedEventId(id); setActiveView("events"); setPreviewEvent(null); }}
        />
      )}
    </div>
  );
}
